import { __decorate, __values, __param, __extends, __read } from 'tslib';
import { CommonModule, DOCUMENT } from '@angular/common';
import { EventEmitter, Input, Output, Component, ElementRef, ContentChildren, HostListener, ViewEncapsulation, NgModule, ChangeDetectionStrategy, HostBinding, Directive, ɵɵdefineInjectable, Injectable, Optional, Inject, ViewChild, TemplateRef, InjectionToken, ViewContainerRef, Renderer2 } from '@angular/core';
import { ListKeyManager, FocusMonitor, A11yModule } from '@angular/cdk/a11y';
import { OverlayPositionBuilder, Overlay, OverlayContainer, OverlayModule, OverlayConfig } from '@angular/cdk/overlay';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { ComponentPortal, TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

/**
 * The prefix to apply to selectors, classnames etc.
 */
var prefix = 'ln';

var AccordionPanel = /** @class */ (function () {
    function AccordionPanel() {
        /**
         * whether or not accordion is disabled
         * Default false
         */
        this.disabled = false;
        /**
         * Event emitted when a panel is clicked
         */
        this.toggle = new EventEmitter();
    }
    /**
     * Toggles the expanded state of panel
     */
    AccordionPanel.prototype.togglePanel = function () {
        this.isExpanded = !this.isExpanded;
        this.toggle.emit(this.isExpanded);
    };
    __decorate([
        Input()
    ], AccordionPanel.prototype, "title", void 0);
    __decorate([
        Input()
    ], AccordionPanel.prototype, "isExpanded", void 0);
    __decorate([
        Input()
    ], AccordionPanel.prototype, "disabled", void 0);
    __decorate([
        Output()
    ], AccordionPanel.prototype, "toggle", void 0);
    AccordionPanel = __decorate([
        Component({
            selector: prefix + "-accordion-panel",
            template: "\n  <button\n  [class." + prefix + "-accordion-expanded]=\"isExpanded\"\n  [id]=\"headerID\"\n  role=\"button\"\n  (click)=\"togglePanel()\"\n  [attr.aria-controls]=\"panelID\"\n  [attr.aria-expanded]=\"isExpanded\"\n  [disabled]=\"disabled\">\n  <span class=\"title\" [innerHTML]=\"title\">\n  </span>\n  <svg [class.hide]=\"isExpanded\" xmlns=\"http://www.w3.org/2000/svg\" width=\"14\" height=\"14\" viewBox=\"0 0 24 24\">\n    <rect width=\"14\" height=\"14\" fill=\"#687C96\" opacity=\"0\"/>\n    <g transform=\"translate(0 6)\">\n        <path d=\"M15.5,21.2l-12-10.33L4.857,9.3,15.5,18.483,26.143,9.3,27.5,10.865Z\" transform=\"translate(-3.5 -9.301)\" fill=\"#687C96\"/>\n    </g>\n  </svg>\n  <svg [class.hide]=\"!isExpanded\" xmlns=\"http://www.w3.org/2000/svg\" width=\"14\" height=\"14\" viewBox=\"0 0 24 24\">\n    <rect width=\"14\" height=\"14\" fill=\"#687C96\" opacity=\"0\"/>\n    <g transform=\"translate(0 6)\">\n        <path d=\"M15.5,9.3l12,10.33L26.143,21.2,15.5,12.013,4.857,21.2,3.5,19.63Z\" transform=\"translate(-3.5 -9.301)\" fill=\"#687C96\"/>\n    </g>\n  </svg>\n</button>\n<div\n  class=\"" + prefix + "-accordion-content\"\n  [class." + prefix + "-accordion-expanded]=\"isExpanded\"\n  [id]=\"panelID\"\n  role=\"region\"\n  [attr.aria-labelledby]=\"headerID\">\n  <ng-content></ng-content>\n</div>\n",
            host: {
                'class': prefix + "-accordion-panel",
                '[class.disabled]': 'disabled',
            }
        })
    ], AccordionPanel);
    return AccordionPanel;
}());

var Accordion = /** @class */ (function () {
    function Accordion(focusMonitor, _elementRef) {
        this.focusMonitor = focusMonitor;
        this._elementRef = _elementRef;
        /**
         * Whether to allow multiple expansions of accordions
         * Default false
         */
        this.allowMultiExpansion = false;
        this.DEFAULT_ID = "accordion_" + Math.random().toString(36).substr(2, 5);
        this.HEADER_ID = '-header-';
        this.PANEL_ID = '-panel-';
    }
    /**
     * @param {Event} event Enables keyboard navigation with up and down arrows
     **/
    Accordion.prototype.onKeyup = function (event) {
        if (event.keyCode === 38 || event.keyCode === 40) {
            this.keyManager.onKeydown(event);
            var activeBtn = this._elementRef.nativeElement.querySelector("button#" + this.keyManager.activeItem.headerID);
            this.focusMonitor.focusVia(activeBtn, 'keyboard');
        }
    };
    Accordion.prototype.ngAfterContentInit = function () {
        var _this = this;
        this.accordion.toArray().forEach(function (panel) {
            // when a panel is clicked, expand the panel content
            panel.toggle.subscribe(function (x) {
                if (x === true) {
                    _this._collapseAll(panel);
                }
            });
        });
    };
    Accordion.prototype.ngAfterViewInit = function () {
        var _this = this;
        /**
         * keyManager is used for up and down arrow navigation options
         */
        this.keyManager = new ListKeyManager(this.accordion);
        this.keyManager.withHorizontalOrientation('ltr');
        this.keyManager.withWrap();
        if (this.accordion) {
            setTimeout(function () {
                _this.intializePanelIDs();
            }, 50);
        }
    };
    /**
     * Initialize panel ids
     * Give ids to each panel and the accordion itself
     */
    Accordion.prototype.intializePanelIDs = function () {
        var _this = this;
        if (!this.id) {
            this.id = this.DEFAULT_ID;
        }
        this.accordion.forEach(function (panel, i) {
            panel.headerID = "" + _this.id + _this.HEADER_ID + i;
            panel.panelID = "" + _this.id + _this.PANEL_ID + i;
        });
    };
    /**
     * Close/Collapse all the accordion panels
     *
     * @param {AccordionPanel} panel current panel
     */
    Accordion.prototype._collapseAll = function (panel) {
        if (!this.allowMultiExpansion) {
            this.accordion.toArray().forEach(function (p) {
                if (p !== panel) {
                    p.isExpanded = false;
                }
            });
        }
    };
    Accordion.ctorParameters = function () { return [
        { type: FocusMonitor },
        { type: ElementRef }
    ]; };
    __decorate([
        Input()
    ], Accordion.prototype, "id", void 0);
    __decorate([
        Input()
    ], Accordion.prototype, "allowMultiExpansion", void 0);
    __decorate([
        ContentChildren(AccordionPanel)
    ], Accordion.prototype, "accordion", void 0);
    __decorate([
        HostListener('window:keyup', ['$event'])
    ], Accordion.prototype, "onKeyup", null);
    Accordion = __decorate([
        Component({
            selector: prefix + "-accordion",
            template: "<ng-content select=\"" + prefix + "-accordion-panel\"></ng-content>",
            encapsulation: ViewEncapsulation.None,
            host: {
                'class': prefix + "-accordion",
                'tabindex': '-1',
                'aria-modal': 'true',
                'role': 'menu',
            },
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-accordion .ln-accordion-panel{display:block;border-top:1px solid #8c9eb1}.ln-accordion .ln-accordion-panel:last-child{border-bottom:1px solid #8c9eb1}.ln-accordion .ln-accordion-panel .ln-accordion-content{opacity:0;max-height:0;transition:opacity .3s,max-height .1s ease-out;background-color:#fff}.ln-accordion .ln-accordion-panel .ln-accordion-content.ln-accordion-expanded{opacity:1;max-height:1000px;padding:24px;transition:max-height .5s ease-in}.ln-accordion .ln-accordion-panel>button{border:2px solid transparent;box-sizing:border-box;text-align:left;position:relative;display:flex;align-items:center;width:100%;padding:24px;background-color:#fff;cursor:pointer}.ln-accordion .ln-accordion-panel>button:focus{border:2px solid #4fc3f7;border-radius:4px;outline:0}.ln-accordion .ln-accordion-panel>button:hover{background-color:#fafafa}.ln-accordion .ln-accordion-panel>button svg{padding-right:8px}.ln-accordion .ln-accordion-panel>button svg.hide{display:none}.ln-accordion .ln-accordion-panel>button .title{width:100%;color:#132a43;font-size:18px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.ln-accordion .ln-accordion-panel>button.ln-accordion-expanded{border-style:solid;border-left-width:2px;border-right-width:2px;background-color:#fff}.ln-accordion .ln-accordion-panel.disabled{opacity:.5}.ln-accordion .ln-accordion-panel.disabled>button{cursor:default}.ln-accordion .ln-accordion-panel.disabled>button:hover{background-color:#fff}"]
        })
    ], Accordion);
    return Accordion;
}());

var AccordionModule = /** @class */ (function () {
    function AccordionModule() {
    }
    AccordionModule = __decorate([
        NgModule({
            declarations: [
                Accordion,
                AccordionPanel,
            ],
            imports: [CommonModule],
            exports: [
                Accordion,
                AccordionPanel,
            ],
        })
    ], AccordionModule);
    return AccordionModule;
}());

/*
 * Public API Surface of accordion
 */

var BUTTON_HOST_ATTRIBUTES = [
    prefix + "-button",
    prefix + "-button-primary",
    prefix + "-button-secondary",
    prefix + "-button-tertiary",
    prefix + "-button-brand",
    prefix + "-button-small",
    prefix + "-button-medium",
    prefix + "-button-large",
];
var Button = /** @class */ (function () {
    function Button(elementRef) {
        var e_1, _a;
        this.elementRef = elementRef;
        try {
            for (var BUTTON_HOST_ATTRIBUTES_1 = __values(BUTTON_HOST_ATTRIBUTES), BUTTON_HOST_ATTRIBUTES_1_1 = BUTTON_HOST_ATTRIBUTES_1.next(); !BUTTON_HOST_ATTRIBUTES_1_1.done; BUTTON_HOST_ATTRIBUTES_1_1 = BUTTON_HOST_ATTRIBUTES_1.next()) {
                var attr = BUTTON_HOST_ATTRIBUTES_1_1.value;
                if (this.elementRef.nativeElement.hasAttribute(attr)) {
                    this.elementRef.nativeElement.classList.add(attr);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (BUTTON_HOST_ATTRIBUTES_1_1 && !BUTTON_HOST_ATTRIBUTES_1_1.done && (_a = BUTTON_HOST_ATTRIBUTES_1.return)) _a.call(BUTTON_HOST_ATTRIBUTES_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        elementRef.nativeElement.classList.add(prefix + "-button");
    }
    Button.ctorParameters = function () { return [
        { type: ElementRef }
    ]; };
    Button = __decorate([
        Component({
            // eslint-disable-next-line @angular-eslint/component-selector
            selector: "button[" + prefix + "-button],\n             button[" + prefix + "-button-primary],\n             button[" + prefix + "-button-secondary],\n             button[" + prefix + "-button-tertiary],\n             button[" + prefix + "-button-brand],\n             button[" + prefix + "-button-small],\n             button[" + prefix + "-button-medium],\n             button[" + prefix + "-button-large]",
            template: '<ng-content></ng-content>',
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-button{border-radius:4px;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;font-weight:700;transition:color 150ms cubic-bezier(.4,0,.2,1),background 150ms cubic-bezier(.4,0,.2,1),box-shadow 150ms cubic-bezier(.4,0,.2,1);height:48px;padding:14px 24px 15px;font-size:16px;border:0 currentcolor;background-color:#00172e;color:#fff}.ln-button.ln-button-small{height:32px;padding:7px 16px 8px;font-size:14px}.ln-button.ln-button-large{height:56px;padding:18px 24px 19px;font-size:16px}.ln-button:hover{background-color:#1a304d;outline:0;box-shadow:0 0 0 2px #eee;border:0 currentcolor}.ln-button:focus{background-color:#1a304d;outline:0;box-shadow:0 0 0 2px #4fc3f7}.ln-button:active{background-color:#2b3b60}.ln-button:disabled{color:#546e7a;pointer-events:none;background:#eceff1}.ln-button.ln-button-secondary{border:2px solid #132a43;background-color:#fff;color:#00172e}.ln-button.ln-button-secondary:hover{background-color:#fff;outline:0;box-shadow:0 0 0 2px #eee;border:2px solid #1a304d}.ln-button.ln-button-secondary:focus{background-color:#fff;outline:0;box-shadow:0 0 0 2px #4fc3f7}.ln-button.ln-button-secondary:disabled{color:#546e7a;pointer-events:none}.ln-button.ln-button-secondary:active{background-color:#fff;border:2px solid #2b3b60;color:#2b3b60}.ln-button.ln-button-secondary:disabled{border-color:#eceff1}.ln-button.ln-button-tertiary{border:0 currentcolor;background-color:#fff;color:#00172e}.ln-button.ln-button-tertiary:hover{background-color:#eee;outline:0;box-shadow:0 0 0 2px #eee;border:0 currentcolor}.ln-button.ln-button-tertiary:focus{background-color:#fff;outline:0;box-shadow:0 0 0 2px #4fc3f7}.ln-button.ln-button-tertiary:disabled{color:#546e7a;pointer-events:none}.ln-button.ln-button-tertiary:active{background-color:#fff;color:#2b3b60}.ln-button.ln-button-brand{background:linear-gradient(to top right,#f6271c,#e91e63)}.ln-button.ln-button-brand:focus,.ln-button.ln-button-brand:hover{background:linear-gradient(to top right,#f3403e,#ec407a)}.ln-button.ln-button-brand:active{background:linear-gradient(to top right,#e8171f,#d81b60)}.ln-button.ln-button-brand:disabled{background:#eceff1}"]
        })
    ], Button);
    return Button;
}());

var ButtonModule = /** @class */ (function () {
    function ButtonModule() {
    }
    ButtonModule = __decorate([
        NgModule({
            declarations: [Button],
            imports: [CommonModule],
            exports: [Button],
        })
    ], ButtonModule);
    return ButtonModule;
}());

/*
 * Public API Surface of button
 */

var Tooltip = /** @class */ (function () {
    function Tooltip() {
        /**
         * content inside the tooltip
         */
        this.content = '';
        this.prefix = prefix;
    }
    __decorate([
        Input()
    ], Tooltip.prototype, "content", void 0);
    __decorate([
        Input()
    ], Tooltip.prototype, "position", void 0);
    __decorate([
        Input()
    ], Tooltip.prototype, "id", void 0);
    Tooltip = __decorate([
        Component({
            selector: prefix + "-tooltip",
            template: "<div role=\"tooltip\" class=\"{{prefix}}-tooltip {{position}}\" id=\"{{id}}\" aria-hidden=\"false\">\n  {{ content }}\n</div>",
            encapsulation: ViewEncapsulation.None,
            changeDetection: ChangeDetectionStrategy.OnPush,
            animations: [trigger('tooltip', [
                    transition(':enter', [
                        style({ opacity: 0 }),
                        animate(300, style({ opacity: 1 })),
                    ]),
                    transition(':leave', [animate(300, style({ opacity: 0 }))]),
                ])],
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-tooltip{font:600 14px/20px Lato;background:#eceff1;color:#455a64;max-width:250px;border-radius:4px;padding:8px}"]
        })
    ], Tooltip);
    return Tooltip;
}());

var TooltipDirective = /** @class */ (function () {
    function TooltipDirective(overlayPositionBuilder, elementRef, overlay, overlayContainer) {
        this.overlayPositionBuilder = overlayPositionBuilder;
        this.elementRef = elementRef;
        this.overlay = overlay;
        this.overlayContainer = overlayContainer;
        /**
         * The position in which the tooltip appears.
         * DEFAULT top
         */
        this.position = 'top';
        this.tooltipDisplayed = false;
        this.ariaDescribedby = prefix + "-tooltips-" + Math.random().toString(36).substr(2, 5);
    }
    Object.defineProperty(TooltipDirective.prototype, "descriptorId", {
        get: function () {
            return this.tooltipDisplayed ? this.ariaDescribedby : null;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Triggered when mouse enters HTML Element
     */
    TooltipDirective.prototype.show = function () {
        if (this.tooltipDisplayed) {
            return;
        }
        var message = this.tooltip != null ? String(this.tooltip).trim() : '';
        if (!message) {
            return;
        }
        var originY = 'bottom';
        var overlayY = 'top';
        var originX = 'center';
        var overlayX = 'center';
        var offsetY = 16;
        var offsetX = 0;
        switch (this.position) {
            case 'top': {
                originY = 'top';
                overlayY = 'bottom';
                offsetY = -16;
                break;
            }
            case 'left': {
                originY = 'center';
                overlayY = 'center';
                originX = 'start';
                overlayX = 'end';
                offsetY = 0;
                offsetX = -16;
                break;
            }
            case 'right': {
                originY = 'center';
                overlayY = 'center';
                originX = 'end';
                overlayX = 'start';
                offsetY = 0;
                offsetX = 16;
                break;
            }
            default: {
                break;
            }
        }
        var positionStrategy = this.overlayPositionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions([{
                originX: originX,
                originY: originY,
                overlayX: overlayX,
                overlayY: overlayY,
                offsetY: offsetY,
                offsetX: offsetX,
            }]);
        this._detach();
        this.overlayRef = this.overlay.create({ positionStrategy: positionStrategy });
        var tooltipRef = this.overlayRef.attach(new ComponentPortal(Tooltip));
        tooltipRef.instance.content = message;
        tooltipRef.instance.position = this.position;
        tooltipRef.instance.id = this.ariaDescribedby;
        this.tooltipDisplayed = true;
    };
    /**
     * Triggered when mouse leaves HTML Element
     */
    TooltipDirective.prototype.hide = function () {
        if (this.tooltipDisplayed) {
            this._detach();
        }
    };
    /**
     * Dispose the tooltip when destroyed.
     */
    TooltipDirective.prototype.ngOnDestroy = function () {
        this.ariaDescribedby = null;
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.tooltipDisplayed = false;
        }
    };
    /**
     * Detaches the currently-attached tooltip.
     */
    TooltipDirective.prototype._detach = function () {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
        this.tooltipDisplayed = false;
    };
    TooltipDirective.ctorParameters = function () { return [
        { type: OverlayPositionBuilder },
        { type: ElementRef },
        { type: Overlay },
        { type: OverlayContainer }
    ]; };
    __decorate([
        Input()
    ], TooltipDirective.prototype, "tooltip", void 0);
    __decorate([
        Input()
    ], TooltipDirective.prototype, "position", void 0);
    __decorate([
        HostBinding('attr.aria-describedby')
    ], TooltipDirective.prototype, "descriptorId", null);
    __decorate([
        HostListener('mouseenter'),
        HostListener('focus')
    ], TooltipDirective.prototype, "show", null);
    __decorate([
        HostListener('mouseleave'),
        HostListener('document:keydown.escape'),
        HostListener('blur')
    ], TooltipDirective.prototype, "hide", null);
    TooltipDirective = __decorate([
        Directive({
            selector: '[tooltip]',
        })
    ], TooltipDirective);
    return TooltipDirective;
}());

var TooltipModule = /** @class */ (function () {
    function TooltipModule() {
    }
    TooltipModule = __decorate([
        NgModule({
            exports: [
                Tooltip,
                TooltipDirective,
            ],
            declarations: [
                Tooltip,
                TooltipDirective,
            ],
            imports: [
                CommonModule,
                OverlayModule,
            ],
            entryComponents: [Tooltip],
        })
    ], TooltipModule);
    return TooltipModule;
}());

var ButtonGroupItem = /** @class */ (function () {
    function ButtonGroupItem() {
        /**
         * whether or not a group button is selected
         * default false
         */
        this.selected = false;
        /**
         * whether or not a group button is disabled
         * default false
         */
        this.disabled = false;
        /**
         * event emitted when a group button is clicked
         */
        this.change = new EventEmitter();
        this.prefix = prefix;
    }
    Object.defineProperty(ButtonGroupItem.prototype, "title", {
        /**
         * whether to display a tooltip over the button
         *
         * @returns {string} title
         */
        get: function () {
            return this._title;
        },
        set: function (value) {
            this._title = value != null ? String(value).trim() : '';
        },
        enumerable: true,
        configurable: true
    });
    /**
     * selects the button and emits the change event
     */
    ButtonGroupItem.prototype.onClick = function () {
        this.selected = true;
        this.change.emit(this.value);
    };
    __decorate([
        Input()
    ], ButtonGroupItem.prototype, "value", void 0);
    __decorate([
        Input(),
        HostBinding('class.selected')
    ], ButtonGroupItem.prototype, "selected", void 0);
    __decorate([
        Input()
    ], ButtonGroupItem.prototype, "disabled", void 0);
    __decorate([
        Output()
    ], ButtonGroupItem.prototype, "change", void 0);
    __decorate([
        HostBinding('id')
    ], ButtonGroupItem.prototype, "id", void 0);
    __decorate([
        Input('title')
    ], ButtonGroupItem.prototype, "title", null);
    ButtonGroupItem = __decorate([
        Component({
            selector: prefix + "-button-group-item",
            template: "\n    <button\n      " + prefix + "-button-tertiary\n      [attr.aria-pressed]=\"selected\"\n      (click)=\"onClick()\"\n      [disabled]=\"disabled\"\n      [ngClass]=\"[prefix + '-button-group-item']\"\n      [tooltip]=\"title\"\n    >\n      <ng-content select=\"[icon]\"></ng-content>\n      <span>\n        <ng-content></ng-content>\n      </span>\n    </button>\n  "
        })
    ], ButtonGroupItem);
    return ButtonGroupItem;
}());

var ButtonGroup = /** @class */ (function () {
    function ButtonGroup() {
        /**
         * sets aria-label
         * default empty string
         */
        this.ariaLabel = '';
        /**
         * event emitted when a button group item is clicked
         */
        this.change = new EventEmitter();
    }
    ButtonGroup.prototype.ngAfterContentInit = function () {
        var _this = this;
        this._setButtonID();
        // subscribe for future additions of buttons
        this.groupBtns.changes.subscribe(function () {
            _this._setButtonID();
        });
    };
    /**
     * releases the current selected button
     *
     * @param {ButtonGroupItem} button current button
     */
    ButtonGroup.prototype._selectButton = function (button) {
        this.change.emit(button.value);
        this.groupBtns.toArray().forEach(function (btn) {
            if (btn !== button) {
                btn.selected = false;
            }
        });
    };
    ButtonGroup.prototype._setButtonID = function () {
        var _this = this;
        // find selected button and subscribe to all for future changes
        this.groupBtns.toArray().forEach(function (button) {
            if (button.selected) {
                _this.selectedButton = button;
            }
            button.change.subscribe(function () {
                _this._selectButton(button);
            });
        });
        // under setTimeout() to avoid "Expression has changed after it was checked" error
        setTimeout(function () {
            _this.groupBtns.toArray().forEach(function (button, i) {
                if (_this.id && button.id !== _this.id + "-button-" + i) {
                    button.id = _this.id + "-button-" + i;
                }
            });
        });
        // if none selected, select 1st button
        if (!this.selectedButton) {
            this.selectedButton = this.groupBtns.toArray()[0];
            this.selectedButton.selected = true;
        }
    };
    __decorate([
        Input(),
        HostBinding('id')
    ], ButtonGroup.prototype, "id", void 0);
    __decorate([
        Input(),
        HostBinding('attr.aria-label')
    ], ButtonGroup.prototype, "ariaLabel", void 0);
    __decorate([
        Output()
    ], ButtonGroup.prototype, "change", void 0);
    __decorate([
        ContentChildren(ButtonGroupItem)
    ], ButtonGroup.prototype, "groupBtns", void 0);
    ButtonGroup = __decorate([
        Component({
            selector: prefix + "-button-group",
            template: "<ng-content select=\"" + prefix + "-button-group-item\"></ng-content>",
            host: {
                class: prefix + "-button-group-items",
                role: 'group',
            },
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-button-group-items ln-button-group-item button.ln-button-group-item{font:bold 16px/24px Lato;color:#00172e;height:40px;padding:8px}.ln-button-group-items ln-button-group-item button.ln-button-group-item ln-icon{font-size:24px;height:24px;width:24px}.ln-button-group-items ln-button-group-item button.ln-button-group-item span{padding:0 8px}.ln-button-group-items ln-button-group-item button.ln-button-group-item ln-icon+span{margin-left:4px}.ln-button-group-items ln-button-group-item button.ln-button-group-item span:empty{display:none}.ln-button-group-items ln-button-group-item.selected button.ln-button-group-item{background-color:#0d47a1;color:#fff}.ln-button-group-items ln-button-group-item:not(:last-child){margin-right:4px}"]
        })
    ], ButtonGroup);
    return ButtonGroup;
}());

var ButtonGroupModule = /** @class */ (function () {
    function ButtonGroupModule() {
    }
    ButtonGroupModule = __decorate([
        NgModule({
            declarations: [
                ButtonGroupItem,
                ButtonGroup,
            ],
            imports: [
                CommonModule,
                ButtonModule,
                TooltipModule,
            ],
            exports: [
                ButtonGroupItem,
                ButtonGroup,
            ],
        })
    ], ButtonGroupModule);
    return ButtonGroupModule;
}());

/*
 * Public API Surface of button-group
 */

var CardTitle = /** @class */ (function () {
    function CardTitle() {
    }
    CardTitle = __decorate([
        Component({
            selector: prefix + "-card-title, [" + prefix + "-card-title]",
            template: '<h3><ng-content></ng-content></h3>',
            host: {
                class: prefix + "-card-title",
            }
        })
    ], CardTitle);
    return CardTitle;
}());
var CardContent = /** @class */ (function () {
    function CardContent() {
    }
    CardContent = __decorate([
        Component({
            selector: prefix + "-card-content",
            template: '<ng-content></ng-content>',
            host: {
                class: prefix + "-card-content",
            }
        })
    ], CardContent);
    return CardContent;
}());
var Card = /** @class */ (function () {
    function Card() {
    }
    Card = __decorate([
        Component({
            selector: prefix + "-card",
            template: "\n  <div class=\"" + prefix + "-card\">\n  <ng-content></ng-content>\n</div>",
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-card{background-color:#fff;padding:32px;border-radius:8px;box-sizing:border-box;width:320px;box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.ln-card-title h3{font:700 20px/1.2 Lato;color:#00172e;margin-bottom:24px}.ln-card-content{font:400 16px/24px Lato;color:#455a64}"]
        })
    ], Card);
    return Card;
}());

var CardModule = /** @class */ (function () {
    function CardModule() {
    }
    CardModule = __decorate([
        NgModule({
            declarations: [
                Card,
                CardContent,
                CardTitle,
            ],
            imports: [CommonModule],
            exports: [
                Card,
                CardContent,
                CardTitle,
            ],
        })
    ], CardModule);
    return CardModule;
}());

/*
 * Public API Surface of global-card
 */

var Checkbox = /** @class */ (function () {
    function Checkbox() {
        /**
         * Whether the checkbox is checked.
         * default false
         */
        this.checked = false;
        /**
         * Whether the checkbox is disabled.
         * default false
         */
        this.disabled = false;
        /**
         * Users can specify the `aria-labelledby` which will be forwarded to the input element.
         * default null
         */
        this.ariaLabelledby = null;
        /**
         * Event emitted when the checkbox's `checked` value changes.
         */
        this.change = new EventEmitter();
        /**
         * Event emitted when the checkbox's `indeterminate` value changes.
         */
        this.indeterminateChange = new EventEmitter();
        this.prefix = prefix;
        this._indeterminate = false;
    }
    Object.defineProperty(Checkbox.prototype, "indeterminate", {
        /**
         *Indicates the indeterminate state of the checkbox
         *
         * @readonly
         * @type {boolean}
         * @memberof Checkbox
         */
        get: function () {
            return this._indeterminate;
        },
        set: function (value) {
            if (value !== this._indeterminate) {
                this._indeterminate = value;
                this.indeterminateChange.emit(value);
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @param {Event} event Stops propagation on the change event
     */
    Checkbox.prototype.onInteractionEvent = function (event) {
        event.stopPropagation();
    };
    /**
     * @param {Event} event Toggles the `checked` state of the checkbox.
     */
    Checkbox.prototype.onInputClick = function (event) {
        event.stopPropagation();
        this.checked = !this.checked;
        this.change.emit(this.checked);
    };
    /**
     * @returns {'true' | 'false' | 'mixed'} Returns the state of aria-checked
     */
    Checkbox.prototype.getAriaChecked = function () {
        if (this.checked) {
            return 'true';
        }
        return this.indeterminate ? 'mixed' : 'false';
    };
    __decorate([
        Input()
    ], Checkbox.prototype, "checked", void 0);
    __decorate([
        Input(),
        HostBinding("class." + prefix + "-checkbox-disabled")
    ], Checkbox.prototype, "disabled", void 0);
    __decorate([
        Input()
    ], Checkbox.prototype, "tabIndex", void 0);
    __decorate([
        Input('aria-labelledby')
    ], Checkbox.prototype, "ariaLabelledby", void 0);
    __decorate([
        Input('aria-describedby')
    ], Checkbox.prototype, "ariaDescribedby", void 0);
    __decorate([
        Output()
    ], Checkbox.prototype, "change", void 0);
    __decorate([
        Output()
    ], Checkbox.prototype, "indeterminateChange", void 0);
    __decorate([
        Input()
    ], Checkbox.prototype, "indeterminate", null);
    Checkbox = __decorate([
        Component({
            selector: prefix + "-checkbox",
            template: "<label class=\"{{prefix}}-checkbox\">\n  <span class=\"{{prefix}}-checkbox-input\">\n    <input\n      type=\"checkbox\"\n      [checked]=\"checked\"\n      name=\"{{prefix}}-c\"\n      [disabled]=\"disabled\"\n      [tabIndex]=\"tabIndex\"\n      [attr.aria-labelledby]=\"ariaLabelledby\"\n      [attr.aria-checked]=\"getAriaChecked()\"\n      [attr.aria-describedby]=\"ariaDescribedby\"\n      [indeterminate]=\"indeterminate\"\n      (change)=\"onInteractionEvent($event)\"\n      (click)=\"onInputClick($event)\">\n    <span class=\"{{prefix}}-checkbox-outline\"></span>\n    <span class=\"{{prefix}}-checkbox-frame\">\n      <svg *ngIf=\"!indeterminate\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 19.459\" aria-hidden=\"true\" focusable=\"false\">\n        <path fill='currentColor' d=\"M22.092,3,8.065,18.081l-5.676-6.73L.2,13.216l7.784,9.243L24.2,4.946Z\" transform=\"translate(-0.2 -3)\"/>\n      </svg>\n      <svg *ngIf=\"indeterminate\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\">\n        <g transform=\"translate(4, 10.5)\">\n          <rect fill=\"currentColor\" width=\"16\" height=\"3\" />\n        </g>\n      </svg>\n    </span>\n  </span>\n  <span class=\"{{prefix}}-checkbox-label\">\n    <ng-content></ng-content>\n  </span>\n</label>",
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}ln-checkbox{display:inline-flex}.ln-checkbox{display:inline-grid;display:-ms-inline-grid;grid-template-columns:min-content auto;-ms-grid-columns:min-content 16px auto;align-items:center;grid-gap:16px;cursor:pointer}.ln-checkbox .ln-checkbox-frame{border-radius:2px;width:20px;height:20px}.ln-checkbox .ln-checkbox-input input:focus~.ln-checkbox-outline,.ln-checkbox .ln-checkbox-input input:hover~.ln-checkbox-outline{height:40px;width:40px;top:-10px;left:-10px}.ln-checkbox .ln-checkbox-input svg{width:16px}.ln-checkbox>*{-ms-grid-row-align:center}.ln-checkbox-disabled .ln-checkbox{cursor:default;pointer-events:none}.ln-checkbox-frame{display:inline-grid;display:-ms-inline-grid;border:2px solid #616161;box-sizing:border-box;position:relative}.ln-checkbox-frame svg{transition:transform .1s ease-in 25ms;transform:scale(0);transform-origin:center center;color:#fff;display:block;margin:auto;position:absolute;top:0;right:0;left:0;bottom:0}.ln-checkbox-round .ln-checkbox .ln-checkbox-frame{border-radius:50%;width:24px;height:24px}.ln-checkbox-round .ln-checkbox .ln-checkbox-input input:focus~.ln-checkbox-outline,.ln-checkbox-round .ln-checkbox .ln-checkbox-input input:hover~.ln-checkbox-outline{height:40px;width:40px;top:-8px;left:-8px}.ln-checkbox-round .ln-checkbox .ln-checkbox-input svg{width:14px}.ln-checkbox-round-small .ln-checkbox .ln-checkbox-frame{border-radius:50%;width:16px;height:16px}.ln-checkbox-round-small .ln-checkbox .ln-checkbox-input input:focus~.ln-checkbox-outline,.ln-checkbox-round-small .ln-checkbox .ln-checkbox-input input:hover~.ln-checkbox-outline{height:32px;width:32px;top:-8px;left:-8px}.ln-checkbox-round-small .ln-checkbox .ln-checkbox-input svg{width:10px}.ln-checkbox-input{display:grid;display:-ms-grid;grid-template-areas:\"checkbox\";position:relative}.ln-checkbox-input>*{-ms-grid-row:1;-ms-grid-column:1;grid-area:checkbox}.ln-checkbox-input input{opacity:0;width:1px;height:1px}.ln-checkbox-input input:not([disabled]):focus~.ln-checkbox-outline,.ln-checkbox-input input:not([disabled]):hover~.ln-checkbox-outline{border-radius:50%;transition:opacity cubic-bezier(0,0,.2,1);position:absolute}.ln-checkbox-input input:not([disabled]):focus~.ln-checkbox-outline{background-color:rgba(0,0,0,.12)}.ln-checkbox-input input:not([disabled]):hover~.ln-checkbox-outline{background-color:rgba(0,0,0,.04)}.ln-checkbox-input input:not([disabled]):focus:checked~.ln-checkbox-outline{background-color:rgba(13,71,161,.12)}.ln-checkbox-input input:not([disabled]):hover:checked~.ln-checkbox-outline{background-color:rgba(13,71,161,.04)}.ln-checkbox-input input:checked~.ln-checkbox-frame,.ln-checkbox-input input:indeterminate~.ln-checkbox-frame{background-color:#0d47a1;border-color:#0d47a1}.ln-checkbox-input input:checked~.ln-checkbox-frame svg,.ln-checkbox-input input:indeterminate~.ln-checkbox-frame svg{transform:scale(1)}.ln-checkbox-input input:not(:checked):disabled~.ln-checkbox-frame,.ln-checkbox-input input:not(:indeterminate):disabled~.ln-checkbox-frame{opacity:.24;border-color:#546e7a}.ln-checkbox-input input:checked:disabled~.ln-checkbox-frame,.ln-checkbox-input input:indeterminate:disabled~.ln-checkbox-frame{opacity:.24;background:#546e7a;border-color:#546e7a}"]
        })
    ], Checkbox);
    return Checkbox;
}());

var CheckboxModule = /** @class */ (function () {
    function CheckboxModule() {
    }
    CheckboxModule = __decorate([
        NgModule({
            declarations: [Checkbox],
            imports: [CommonModule],
            exports: [Checkbox],
        })
    ], CheckboxModule);
    return CheckboxModule;
}());

/*
 * Public API Surface of checkbox
 */

/**
 * Service to register and display icons used by the `<ln-icon>` component.
 */
var IconsRegistry = /** @class */ (function () {
    function IconsRegistry() {
        this.registry = new Map();
    }
    IconsRegistry.prototype.registerIcons = function (icons) {
        var _this = this;
        icons.forEach(function (icon) { return _this.registry.set(icon.name, icon.data); });
    };
    IconsRegistry.prototype.getIcon = function (iconName) {
        if (!this.registry.has(iconName)) {
            throw new Error("Icon with the name " + iconName + " not found. Make sure to add it to the Icon registry.");
        }
        return this.registry.get(iconName);
    };
    IconsRegistry.ngInjectableDef = ɵɵdefineInjectable({ factory: function IconsRegistry_Factory() { return new IconsRegistry(); }, token: IconsRegistry, providedIn: "root" });
    IconsRegistry = __decorate([
        Injectable({
            providedIn: 'root',
        })
    ], IconsRegistry);
    return IconsRegistry;
}());

var Icon = /** @class */ (function () {
    function Icon(element, iconRegistry, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    document) {
        this.element = element;
        this.iconRegistry = iconRegistry;
        this.document = document;
        this.sizeConfig = {
            sm: '16px',
            md: '24px',
            lg: '32px',
        };
        this._size = 'md';
    }
    Object.defineProperty(Icon.prototype, "name", {
        /**
         * Name of the icon in the SVG icon set.
         *
         * @param {string} iconName name of the SVG icon
         */
        set: function (iconName) {
            if (this.svgIcon) {
                this.element.nativeElement.removeChild(this.svgIcon);
            }
            var svgData = this.iconRegistry.getIcon(iconName);
            this.svgIcon = this.svgElementFromString(svgData);
            this.element.nativeElement.setAttribute('aria-label', iconName + "-icon");
            this.element.nativeElement.appendChild(this.svgIcon);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Icon.prototype, "size", {
        /**
         * Size of the svg icon to display.
         * default 'md' (24px X 24px)
         *
         * @returns {"sm" | "md" | "lg" | number} The size of the SVG icon
         */
        get: function () {
            return this._size;
        },
        set: function (value) {
            this.element.nativeElement.style.width = this.configSize(value);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Creates a DOM element from the given SVG string.
     *
     * @param {string} svgContent The SVG content
     *
     * @returns {SVGElement} The SVG element
     */
    Icon.prototype.svgElementFromString = function (svgContent) {
        var div = this.document.createElement('DIV');
        div.innerHTML = svgContent;
        var svg = div.querySelector('svg');
        if (!svg) {
            throw Error('<svg> tag not found');
        }
        return svg;
    };
    Icon.prototype.coerceCssPixelValue = function (value) {
        if (value == null) {
            return '';
        }
        return typeof value === 'string' ? value : value + "px";
    };
    // get size from config if value is string, else coerceCssPixelValue
    Icon.prototype.configSize = function (value) {
        if (this.sizeConfig[value]) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return this.sizeConfig[value];
        }
        return this.coerceCssPixelValue(value);
    };
    Icon.ctorParameters = function () { return [
        { type: ElementRef },
        { type: IconsRegistry },
        { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT,] }] }
    ]; };
    __decorate([
        Input()
    ], Icon.prototype, "icon", void 0);
    __decorate([
        Input()
    ], Icon.prototype, "iconSet", void 0);
    __decorate([
        Input()
    ], Icon.prototype, "name", null);
    __decorate([
        Input()
    ], Icon.prototype, "size", null);
    Icon = __decorate([
        Component({
            selector: prefix + "-icon",
            template: '<ng-content></ng-content>',
            host: {
                '[class]': "icon ? iconSet + \" \" + icon : '" + prefix + "-icon'",
                'role': 'img',
                'aria-hidden': 'true',
            },
            encapsulation: ViewEncapsulation.None,
            changeDetection: ChangeDetectionStrategy.OnPush,
            styles: ["\n    .ln-icon {\n      display: inline-block;\n      fill: currentColor;\n      width: 24px;\n      }\n  "]
        }),
        __param(2, Optional()), __param(2, Inject(DOCUMENT))
    ], Icon);
    return Icon;
}());

var IconModule = /** @class */ (function () {
    function IconModule() {
    }
    IconModule = __decorate([
        NgModule({
            declarations: [Icon],
            imports: [CommonModule],
            exports: [Icon],
        })
    ], IconModule);
    return IconModule;
}());

/*
 * Public API Surface of icon
 */

/**
 * While this component can be used alone, it also provides a number
 * of preset styles for common modal sections, including:
 * - ${prefix}-modal-header
 * - ${prefix}-modal-content
 * - ${prefix}-modal-actions
 */
var Modal = /** @class */ (function () {
    function Modal(eRef) {
        this.eRef = eRef;
        /**
         * Size of the modal to display: x-small, small, medium, large, x-large
         * Default Medium
         */
        this.size = 'md';
        /**
         * whether or not to display overlay
         * Default true
         */
        this.useoverlay = true;
        /**
         * whether or not to allow pressing of escape button to close the modal
         * Default true
         */
        this.allowescape = true;
        /**
         * whether or not the close icon should be displayed
         * Default true
         */
        this.closebtn = true;
        /**
         * aria label for the close button
         * Default 'Close'
         */
        this.closebtnArialabel = 'Close';
        /**
         * Event emitted when the modal is closed by clicking on cancel button or close button.
         */
        this.closed = new EventEmitter();
        this.prefix = prefix;
        /**
         * Indicator if the modal is opened.
         * Default false
         */
        this._opened = false;
    }
    /**
     * Listen to escape key and close the modal
     */
    Modal.prototype.onKeyDown = function () {
        if (!this.allowescape || !this._opened) {
            return;
        }
        this.cancel();
    };
    /**
     * Open the modal
     */
    Modal.prototype.open = function () {
        this._opened = true;
        // adds eventListener to modal, to track mouse clicks
        if (this.allowescape) {
            this.addEventListener();
        }
    };
    /**
     * Close the modal, but don't send the confirm
     */
    Modal.prototype.cancel = function () {
        this._opened = false;
        this.removeEventListener();
        this.closed.emit();
    };
    /**
     * Add eventListener that closes the modal, when clicked outside of the model
     */
    Modal.prototype.addEventListener = function () {
        var _this = this;
        this.eventlistener = function (event) {
            if (!_this.eRef.nativeElement.lastElementChild.contains(event.target)) {
                _this.cancel();
            }
        };
        this.eRef.nativeElement.addEventListener('click', this.eventlistener);
    };
    /**
     * Remove eventListener that closes the modal, when clicked outside of the model
     */
    Modal.prototype.removeEventListener = function () {
        this.eRef.nativeElement.removeEventListener('click', this.eventlistener);
    };
    Modal.ctorParameters = function () { return [
        { type: ElementRef }
    ]; };
    __decorate([
        Input()
    ], Modal.prototype, "size", void 0);
    __decorate([
        Input()
    ], Modal.prototype, "useoverlay", void 0);
    __decorate([
        Input()
    ], Modal.prototype, "allowescape", void 0);
    __decorate([
        Input()
    ], Modal.prototype, "closebtn", void 0);
    __decorate([
        Input()
    ], Modal.prototype, "closebtnArialabel", void 0);
    __decorate([
        Output()
    ], Modal.prototype, "closed", void 0);
    __decorate([
        HostListener('document:keydown.escape')
    ], Modal.prototype, "onKeyDown", null);
    Modal = __decorate([
        Component({
            selector: prefix + "-modal",
            template: "<div *ngIf=\"useoverlay && _opened\" class=\"{{prefix}}-modal-overlay\"></div>\n<div *ngIf=\"_opened\" role=\"dialog\" [ngClass]=\"[prefix + '-modal', prefix + '-modal-' + size]\" cdkTrapFocus cdkTrapFocusAutoCapture>\n  <button *ngIf=\"closebtn\" (click)=\"cancel()\" class=\"{{prefix}}-modal-close\" [attr.aria-label]=\"closebtnArialabel\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"10.828\" height=\"10.828\" viewBox=\"0 0 10.828 10.828\">\n      <g transform=\"translate(-111.586 -130.586)\">\n        <g transform=\"translate(105 124)\">\n          <line class=\"a\" x1=\"8\" y2=\"8\" transform=\"translate(8 8)\" />\n          <line class=\"a\" x1=\"8\" y1=\"8\" transform=\"translate(8 8)\" />\n        </g>\n      </g>\n    </svg>\n  </button>\n  <ng-content></ng-content>\n</div>",
            encapsulation: ViewEncapsulation.None,
            host: {
                'class': prefix + "-modal-container",
                'tabindex': '-1',
                'aria-modal': 'true',
            },
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-modal-overlay{width:100vw;height:100vh;position:fixed;top:0;bottom:0;left:0;right:0;z-index:49;background-color:rgba(0,0,0,.64)}.ln-modal{padding:32px;margin:0 auto;z-index:50;background-color:#fff;box-sizing:content-box;display:flex;flex-direction:column;border-radius:8px;box-shadow:0 24px 38px rgba(0,0,0,.14);position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);max-height:calc(100vh - (2 * 32px))}.ln-modal .ln-modal-close{background-color:#fafafa;border-radius:50%;cursor:pointer;width:32px;height:32px;position:absolute;right:32px;top:32px}.ln-modal .ln-modal-close:hover{background-color:#f5f5f5}.ln-modal .ln-modal-close:active{background-color:#eee}.ln-modal .ln-modal-close:focus{background-color:#fff;border:2px solid #4fc3f7}.ln-modal .ln-modal-close svg .a{fill:none;stroke:#455a64;stroke-linecap:round;stroke-linejoin:round;stroke-width:2px}.ln-modal .ln-modal-header{color:#00172e}.ln-modal .ln-modal-header h2{font:bold 24px/30px Lato;margin-top:0}.ln-modal .ln-modal-content{font:16px/24px Lato;margin-bottom:16px}.ln-modal .ln-modal-actions{text-align:right;padding-top:16px}.ln-modal .ln-modal-actions .ln-button+.ln-button{margin-left:16px}@media screen and (max-width:759px){.ln-modal{width:100%}}.ln-modal.ln-modal-xl{min-width:1090px;max-width:1448px}.ln-modal.ln-modal-lg{min-width:968px;max-width:1090px}.ln-modal.ln-modal-md{min-width:648px;max-width:968px}.ln-modal.ln-modal-sm{min-width:378px;max-width:648px}.ln-modal.ln-modal-xs{min-width:378px;max-width:378px}"]
        })
    ], Modal);
    return Modal;
}());
/**
 * Header text container
 */
var ModalHeader = /** @class */ (function () {
    function ModalHeader() {
    }
    ModalHeader = __decorate([
        Component({
            selector: prefix + "-modal-header, [" + prefix + "-modal-header]",
            template: "\n  <header class=\"" + prefix + "-modal-header\">\n    <h2><ng-content></ng-content></h2>\n  </header>"
        })
    ], ModalHeader);
    return ModalHeader;
}());
/**
 * Main content container
 */
var ModalContent = /** @class */ (function () {
    function ModalContent() {
    }
    ModalContent = __decorate([
        Component({
            selector: prefix + "-modal-content, [" + prefix + "-modal-content]",
            template: "<section class=\"" + prefix + "-modal-content\"><ng-content></ng-content></section>"
        })
    ], ModalContent);
    return ModalContent;
}());
/**
 * Action button container
 */
var ModalActions = /** @class */ (function () {
    function ModalActions() {
    }
    ModalActions = __decorate([
        Component({
            selector: prefix + "-modal-actions, [" + prefix + "-modal-actions]",
            template: "<footer class=\"" + prefix + "-modal-actions\"><ng-content></ng-content></footer>"
        })
    ], ModalActions);
    return ModalActions;
}());

var ModalModule = /** @class */ (function () {
    function ModalModule() {
    }
    ModalModule = __decorate([
        NgModule({
            declarations: [
                Modal,
                ModalHeader,
                ModalContent,
                ModalActions,
            ],
            imports: [
                CommonModule,
                A11yModule,
            ],
            exports: [
                Modal,
                ModalHeader,
                ModalContent,
                ModalActions,
            ],
        })
    ], ModalModule);
    return ModalModule;
}());

/*
 * Public API Surface of modal
 */

var NOTIFICATION_STATE = [
    'informative',
    'cautionary',
    'positive',
    'critical',
];
var Notification = /** @class */ (function () {
    function Notification(elementRef) {
        var e_1, _a;
        this.elementRef = elementRef;
        /**
         * Type of a notification
         * default inline
         */
        this.type = 'inline';
        /**
         * Whether to display the close icon
         * default false
         */
        this.showClose = false;
        /**
         * Event emitted on click of close icon
         */
        this.closed = new EventEmitter();
        /**
         * Event emitted on action button click
         */
        this.actionClicked = new EventEmitter();
        this.notificationState = 'informative';
        this.prefix = prefix;
        this._actionLabel = '';
        try {
            for (var NOTIFICATION_STATE_1 = __values(NOTIFICATION_STATE), NOTIFICATION_STATE_1_1 = NOTIFICATION_STATE_1.next(); !NOTIFICATION_STATE_1_1.done; NOTIFICATION_STATE_1_1 = NOTIFICATION_STATE_1.next()) {
                var attr = NOTIFICATION_STATE_1_1.value;
                if (this.elementRef.nativeElement.hasAttribute(attr)) {
                    this.elementRef.nativeElement.classList.add(attr);
                    this.notificationState = attr;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (NOTIFICATION_STATE_1_1 && !NOTIFICATION_STATE_1_1.done && (_a = NOTIFICATION_STATE_1.return)) _a.call(NOTIFICATION_STATE_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    Object.defineProperty(Notification.prototype, "actionLabel", {
        /**
         * Action label
         *
         * @returns {string} action label
         */
        get: function () {
            return this._actionLabel;
        },
        set: function (value) {
            this._actionLabel = value != null ? String(value).trim() : '';
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Emit the closed event
     */
    Notification.prototype.onClose = function () {
        this.closed.emit();
    };
    /**
     * Emits event on action click
     */
    Notification.prototype.onActionClick = function () {
        this.actionClicked.emit();
    };
    Notification.ctorParameters = function () { return [
        { type: ElementRef }
    ]; };
    __decorate([
        Input()
    ], Notification.prototype, "type", void 0);
    __decorate([
        Input()
    ], Notification.prototype, "title", void 0);
    __decorate([
        Input()
    ], Notification.prototype, "showClose", void 0);
    __decorate([
        Output()
    ], Notification.prototype, "closed", void 0);
    __decorate([
        Output()
    ], Notification.prototype, "actionClicked", void 0);
    __decorate([
        Input('actionLabel')
    ], Notification.prototype, "actionLabel", null);
    Notification = __decorate([
        Component({
            selector: prefix + "-notification,\n             " + prefix + "-notification[informative],\n             " + prefix + "-notification[cautionary],\n             " + prefix + "-notification[positive],\n             " + prefix + "-notification[critical]",
            template: "\n  <svg class=\"notification-state\" *ngIf=\"notificationState === 'informative'\" xmlns=\"http://www.w3.org/2000/svg\" width=\"24.289\" height=\"24\" viewBox=\"0 0 24.289 24\">\n  <rect class=\"a\" width=\"24\" height=\"24\" />\n  <g transform=\"translate(0.289)\">\n    <path class=\"b\"\n          d=\"M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm2.658,18.284c-.661.26-2.952,1.354-4.272.191a1.676,1.676,0,0,1-.59-1.318,15.978,15.978,0,0,1,.919-3.957,5.7,5.7,0,0,0,.231-1.313c0-.7-.266-.887-.987-.887a3.31,3.31,0,0,0-1.1.257l.195-.8a7.64,7.64,0,0,1,2.621-.71c1.269,0,2.2.633,2.2,1.837A5.585,5.585,0,0,1,13.7,12.96l-.73,2.582c-.151.522-.424,1.673,0,2.014a2.214,2.214,0,0,0,1.887-.071ZM13.452,8a1.5,1.5,0,1,1,1.5-1.5A1.5,1.5,0,0,1,13.452,8Z\" />\n  </g>\n</svg>\n<svg class=\"notification-state\" *ngIf=\"notificationState === 'positive'\" xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\">\n  <g transform=\"translate(15219 4666)\">\n    <rect class=\"a\"  transform=\"translate(-15219 -4666)\" />\n    <g transform=\"translate(-15219 -4666)\">\n      <path class=\"b\" d=\"M12,0A12,12,0,1,0,24,12,12,12,0,0,0,12,0ZM10,17.414,4.586,12,6,10.586l4,4,8-8L19.414,8Z\" />\n    </g>\n  </g>\n</svg>\n<svg class=\"notification-state\" *ngIf=\"notificationState === 'cautionary'\" xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\">\n  <g transform=\"translate(15219 4620)\">\n    <rect class=\"a\" transform=\"translate(-15219 -4620)\" />\n    <g transform=\"translate(-15219.004 -4620)\">\n      <path class=\"b\"\n            d=\"M23.641,18.485,14.732,1.643a3.093,3.093,0,0,0-5.464,0L.359,18.485A3.079,3.079,0,0,0,3.092,23H20.908a3.079,3.079,0,0,0,2.733-4.515ZM12,20a1.5,1.5,0,1,1,1.5-1.5A1.5,1.5,0,0,1,12,20Zm1-5H11l-.5-8h3Z\" />\n    </g>\n  </g>\n</svg>\n<svg class=\"notification-state\" *ngIf=\"notificationState === 'critical'\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\">\n  <g transform=\"translate(15219 4575)\">\n    <rect class=\"a\" transform=\"translate(-15219 -4575)\" />\n    <g transform=\"translate(-15219 -4575)\">\n      <path class=\"b\"\n            d=\"M22,1H2A1,1,0,0,0,1,2V22a1,1,0,0,0,1,1H22a1,1,0,0,0,1-1V2A1,1,0,0,0,22,1ZM13.645,5,13,14H11l-.608-9ZM12,20a2,2,0,1,1,2-2A2,2,0,0,1,12,20Z\" />\n    </g>\n  </g>\n</svg>\n<div class=\"" + prefix + "-divider\"></div>\n<div class=\"content-wrapper\">\n  <h5 class=\"h5 " + prefix + "-notification-title\">{{ title }}</h5>\n  <div class=\"" + prefix + "-notification-message\">\n    <ng-content></ng-content>\n  </div>\n</div>\n<button *ngIf=\"actionLabel\" " + prefix + "-button-small " + prefix + "-button-secondary [ngClass]=\"[prefix + '-notification-actions']\" (click)=\"onActionClick()\">{{ actionLabel }}</button>\n<div class=\"" + prefix + "-divider button-divider\"></div>\n<button *ngIf=\"showClose\" " + prefix + "-button-small " + prefix + "-button-tertiary class=\"dismiss-button\" (click)=\"onClose()\"><!-- TODO will be replaced with link-button-->\n  <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"10.828\" height=\"10.828\" viewBox=\"0 0 10.828 10.828\">\n    <g transform=\"translate(-111.586 -130.586)\">\n      <g transform=\"translate(105 124)\">\n        <line class=\"a\" x1=\"8\" y2=\"8\" transform=\"translate(8 8)\" />\n        <line class=\"a\" x1=\"8\" y1=\"8\" transform=\"translate(8 8)\" />\n      </g>\n    </g>\n  </svg>\n</button>\n",
            host: {
                role: 'alert',
                class: prefix + "-notification",
            },
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-notification{display:flex;flex-direction:column;background-color:#fff;padding:24px;border-radius:8px;margin:24px 0;position:relative;box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.ln-notification svg.notification-state{margin-bottom:24px;flex-shrink:0}.ln-notification svg.notification-state .a{fill:#fff}.ln-notification svg.notification-state .b{fill:#2196f3}.ln-notification.positive svg .b{fill:#4caf50}.ln-notification.cautionary svg .b{fill:#ffb300}.ln-notification.critical svg .b{fill:#e8171f}.ln-notification .content-wrapper{display:flex;flex-wrap:wrap;flex-grow:1}.ln-notification .content-wrapper .ln-notification-title{color:#00172e;margin-bottom:8px}.ln-notification .content-wrapper .ln-notification-message{font:400 16px/24px Lato;color:#455a64}.ln-notification .ln-notification-actions{margin-top:32px;flex-shrink:0}.ln-notification .dismiss-button{position:absolute;top:24px;right:24px;flex-shrink:0}.ln-notification .dismiss-button.ln-button{padding:8px;width:32px;border-radius:50%;background-color:#fafafa;box-shadow:0 0 2px #fafafa}.ln-notification .dismiss-button svg{fill:none;stroke:#455a64;stroke-linecap:round;stroke-linejoin:round;stroke-width:2px}.ln-notification .ln-divider{border-left:1px solid #eceff1;flex-shrink:0;-ms-grid-row-align:stretch;align-self:stretch;min-height:32px;margin-left:24px;display:none}@media (min-width:600px){.ln-notification{flex-direction:row;align-items:center;max-width:100%}.ln-notification svg.notification-state{margin-bottom:0}.ln-notification .content-wrapper{margin-left:24px}.ln-notification .content-wrapper .ln-notification-title{margin-bottom:0;margin-right:16px}.ln-notification .ln-notification-actions{margin-top:0;margin-left:24px}.ln-notification .dismiss-button{position:static;margin-left:24px}.ln-notification .ln-divider{display:flex}.ln-notification .button-divider{display:none;-ms-grid-row-align:center;align-self:center}.ln-notification .ln-notification-actions:nth-last-child(n+3)~.button-divider:nth-last-child(n+2){display:flex}}"]
        })
    ], Notification);
    return Notification;
}());

var NotificationModule = /** @class */ (function () {
    function NotificationModule() {
    }
    NotificationModule = __decorate([
        NgModule({
            imports: [
                CommonModule,
                ButtonModule,
            ],
            declarations: [Notification],
            exports: [Notification],
        })
    ], NotificationModule);
    return NotificationModule;
}());

/*
 * Public API Surface of notification
 */

var Pagination = /** @class */ (function () {
    function Pagination() {
        /** Page index of the displayed list of items. Defaulted to 1 */
        this.currentPage = 1;
        /** Change event emitted when the user navigates to another page. */
        this.change = new EventEmitter();
        this.prefix = prefix;
    }
    Object.defineProperty(Pagination.prototype, "pageNums", {
        /**
         * Get the list of page numbers to display
         * First page and last page will be displayed by default
         *
         * @returns {number[]} list of page numbers to display
         */
        get: function () {
            var _this = this;
            var range = [];
            var right;
            var left;
            /**
             * Scenario A
             * If there are 1-7 total pages, all page numbers display without ellipsis
             * range = [2, 3, 4, 5, 6]
             * display = 1, 2, 3, [4], 5, 6, 7
             * here page 4 is selected but any page could be without changing the range
             *
             * Scenario B
             * If there are 8 or more pages, and the selected page is 5 or less, the first 5 pages display with ellipsis to the last page
             * range = [2, 3, 4, 5]
             * display = 1 [2], 3, 4, 5 ... [this.totalPages]
             *
             * Scenario C
             * If there are 8 or more pages, and the selected page is greater than the last page - 4,
             * the first page displays with ellipsis to the last 5 pages
             * range = [6, 7, 8, 9]
             * display 1 ... [6], 7, 8, 9, 10
             *
             * Scenario D
             * If there are 10 or more pages, and the selected page is > 5 and < totalPages - 4
             * the first page displays with ellipsis to the selected page surrounded by -1 and +1, and ellipsis to the last page
             * range = [4, 5, 6]
             * display 1 ... 4, [5], 6 ... 10
             */
            // Scenarios A and B - show 5 leading pages
            if (this.currentPage >= 1 && this.currentPage < 5) {
                left = this.currentPage - 2;
                // 7 total pages needs just a little different calculation to display without skipping any numbers
                if (this.totalPages === 7) {
                    right = this.currentPage + 5;
                    for (var i = 1; i <= 7; i++) {
                        if (i === 1 || i >= left && i <= right) {
                            range.push(i);
                        }
                    }
                }
                else {
                    right = this.currentPage + 4;
                    for (var i = 1; i <= 5; i++) {
                        if (i === 1 || i >= left && i <= right) {
                            range.push(i);
                        }
                    }
                }
            }
            else if (this.currentPage > this.totalPages - 4) {
                // Scenarios A and C - show 5 trailing pages
                right = this.currentPage + 2;
                // 7 total pages needs just a little different calculation to display without skipping any numbers
                if (this.totalPages === 7) {
                    left = this.currentPage - 5;
                    for (var i = this.totalPages - 5; i <= this.totalPages; i++) {
                        if (i === this.totalPages || i >= left && i <= right) {
                            range.push(i);
                        }
                    }
                }
                else {
                    left = this.currentPage - 4;
                    for (var i = this.totalPages - 4; i <= this.totalPages; i++) {
                        if (i === this.totalPages || i >= left && i <= right) {
                            range.push(i);
                        }
                    }
                }
            }
            else {
                // Scenario D
                // this block handles any total pages 10 and over, where the selected page is over 5, and the selected page is < totalPages - 4
                range = [
                    this.currentPage - 1,
                    this.currentPage,
                    this.currentPage + 1,
                ];
            }
            return range.filter(function (x) { return x > 1 && x < _this.totalPages; });
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Go to the first page
     */
    Pagination.prototype.firstPage = function () {
        this.currentPage = 1;
        this._emitPageChangeEvent();
    };
    /**
     * Go to the last page.
     */
    Pagination.prototype.lastPage = function () {
        this.currentPage = this.totalPages;
        this._emitPageChangeEvent();
    };
    /**
     * Go to the target page.
     *
     * @param {number} page page number
     */
    Pagination.prototype.goToPage = function (page) {
        this.currentPage = page;
        this._emitPageChangeEvent();
    };
    /** Go to the previous page. */
    Pagination.prototype.previousPage = function () {
        if (!this.hasPreviousPage()) {
            return;
        }
        this.currentPage = this.currentPage - 1;
        this._emitPageChangeEvent();
    };
    /** Go to the next page. */
    Pagination.prototype.nextPage = function () {
        if (!this.hasNextPage()) {
            return;
        }
        this.currentPage = this.currentPage + 1;
        this._emitPageChangeEvent();
    };
    /**
     * Whether there is a previous page.
     *
     * @returns {boolean} indicator if there is a previous page.
     */
    Pagination.prototype.hasPreviousPage = function () {
        return this.currentPage > 1;
    };
    /**
     * Whether there is a next page.
     *
     * @returns {boolean} indicator if there is a next page.
     */
    Pagination.prototype.hasNextPage = function () {
        return this.currentPage < this.totalPages;
    };
    /**
     * Checks whether to display leading ellipsis.
     *
     * @returns {boolean} indicator whether to display leading ellipsis.
     */
    Pagination.prototype._showLeadingEllipsis = function () {
        return this.currentPage > 4 && this.totalPages >= 8;
    };
    /**
     * Checks whether to display trailing ellipsis.
     *
     * @returns {boolean} indicator whether to display trailing ellipsis.
     */
    Pagination.prototype._showTrailingEllipsis = function () {
        return this.currentPage < this.totalPages - 3 && this.totalPages >= 8;
    };
    /**
     * Checks whether the button for going to previous page should be disabled.
     *
     * @returns {boolean} indicator whether the previsous button be disabled.
     */
    Pagination.prototype._previousButtonDisabled = function () {
        return !this.hasPreviousPage();
    };
    /**
     * Checks whether the button for going to next page should be disabled.
     *
     * @returns {boolean} indicator whether the next button be disabled.
     */
    Pagination.prototype._nextButtonDisabled = function () {
        return !this.hasNextPage();
    };
    /**
     * Emits an event notifying that page has been changed.
     */
    Pagination.prototype._emitPageChangeEvent = function () {
        this.change.emit(this.currentPage);
    };
    __decorate([
        Input()
    ], Pagination.prototype, "currentPage", void 0);
    __decorate([
        Input()
    ], Pagination.prototype, "totalPages", void 0);
    __decorate([
        Output()
    ], Pagination.prototype, "change", void 0);
    Pagination = __decorate([
        Component({
            selector: prefix + "-pagination",
            template: "<button class=\"{{prefix}}-pagination-navigation {{prefix}}-pagination-previous\" (click)=\"previousPage()\" [disabled]=\"_previousButtonDisabled()\">\n  <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"6.414\" height=\"10.828\" viewBox=\"0 0 6.414 10.828\">\n    <g transform=\"translate(-17 -14.586)\">\n      <g transform=\"translate(8 8)\">\n        <path class=\"arrow\" d=\"M14,16l-4-4,4-4\"/>\n      </g>\n    </g>\n    </svg>\n</button>\n<button class=\"{{prefix}}-pagination-navigation {{prefix}}-pagination-firstPage\" (click)=\"firstPage()\" [class.selected]=\"currentPage === 1\">\n  1\n</button>\n<span class=\"{{prefix}}-pagination-navigation\" *ngIf=\"_showLeadingEllipsis()\"> ... </span>\n<button\n  *ngFor=\"let page of pageNums\"\n  [class.selected]=\"currentPage === page\"\n  class=\"{{prefix}}-pagination-navigation\"\n  (click)=\"goToPage(page)\">\n  {{ page }}\n</button>\n<span class=\"{{prefix}}-pagination-navigation\" *ngIf=\"_showTrailingEllipsis()\"> ... </span>\n<button\n  (click)=\"lastPage()\"\n  class=\"{{prefix}}-pagination-navigation {{prefix}}-pagination-lastPage\"\n  [class.selected]=\"currentPage === totalPages\">\n  {{ totalPages }}\n</button>\n<button (click)=\"nextPage()\" class=\"{{prefix}}-pagination-navigation {{prefix}}-pagination-next\" [disabled]=\"_nextButtonDisabled()\">\n  <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"6.414\" height=\"10.828\" viewBox=\"0 0 6.414 10.828\">\n      <g transform=\"translate(-16.586 -14.586)\">\n        <g transform=\"translate(8 8)\">\n          <path class=\"arrow\" d=\"M10,8l4,4-4,4\"/>\n        </g>\n      </g>\n    </svg>\n</button>\n",
            host: {
                class: prefix + "-pagination",
                role: 'navigation',
            },
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-pagination{display:flex;justify-content:center;align-items:center;width:100%;flex-wrap:wrap}.ln-pagination .ln-pagination-navigation{box-sizing:border-box;padding:12px;width:40px;font:700 14px/16px Lato;color:#455a64;background:0 0;border-radius:4px;text-align:center}.ln-pagination .ln-pagination-navigation .arrow{fill:none;stroke:#455a64;stroke-linecap:round;stroke-linejoin:round;stroke-width:2px}.ln-pagination .ln-pagination-navigation+.ln-pagination-navigation{margin-left:4px}.ln-pagination button.ln-pagination-navigation{cursor:pointer}.ln-pagination button.ln-pagination-navigation:disabled{cursor:default}.ln-pagination button.ln-pagination-navigation:focus{outline:0}.ln-pagination button.ln-pagination-navigation:focus .arrow{stroke:#00172e}"]
        })
    ], Pagination);
    return Pagination;
}());

var PaginationModule = /** @class */ (function () {
    function PaginationModule() {
    }
    PaginationModule = __decorate([
        NgModule({
            declarations: [Pagination],
            imports: [CommonModule],
            exports: [Pagination],
        })
    ], PaginationModule);
    return PaginationModule;
}());

/*
 * Public API Surface of pagination
 */

// Reusable animation timings
var animationTimings = '300ms cubic-bezier(0.645, 0.045, 0.355, 1)';
var animationTop = { transform: 'translate3d(0, -5%, 0)' };
var animationBottom = { transform: 'translate3d(0, 5%, 0)' };
var animationRight = { transform: 'translate3d(5%, 0, 0)' };
var animationLeft = { transform: 'translate3d(-5%, 0, 0)' };
/**
 * A basic content container component that adds the LN Design System styles of a popover.
 *
 * While this component can be used alone, it also provides a number
 * of preset styles for common popover sections, including:
 * - ${prefix}-popover-image
 * - ${prefix}-popover-header
 * - ${prefix}-popover-title
 * - ${prefix}-popover-body
 * - ${prefix}-popover-actions
 */
var PopoverBaseDirective = /** @class */ (function () {
    function PopoverBaseDirective() {
        /**
         * Position of the popover
         * Default 'top'
         */
        this.position = 'top';
        /**
         * Orientation of the popover
         * Default 'portrait'
         */
        this.orientation = 'portrait';
        /**
         * Defines how the popover is triggered.
         * default trigger
         */
        this.trigger = 'hover';
    }
    __decorate([
        ViewChild(TemplateRef, { static: true })
    ], PopoverBaseDirective.prototype, "templateRef", void 0);
    __decorate([
        Input()
    ], PopoverBaseDirective.prototype, "position", void 0);
    __decorate([
        Input()
    ], PopoverBaseDirective.prototype, "orientation", void 0);
    __decorate([
        Input()
    ], PopoverBaseDirective.prototype, "trigger", void 0);
    PopoverBaseDirective = __decorate([
        Directive({
            selector: prefix + "-popover",
        })
    ], PopoverBaseDirective);
    return PopoverBaseDirective;
}());
var Popover = /** @class */ (function (_super) {
    __extends(Popover, _super);
    function Popover() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Popover = __decorate([
        Component({
            selector: prefix + "-popover",
            template: "\n  <ng-template>\n  <div role=\"tooltip\" aria-modal=\"true\" id=\"{{ id }}\" class=\"" + prefix + "-popover {{position}} {{orientation}}\"\n  [@slideContent]=\"animationState\" @fade>\n    <ng-content select=\"[" + prefix + "-popover-image]\"></ng-content>\n    <div class=\"" + prefix + "-popover-content-container\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</ng-template>\n",
            encapsulation: ViewEncapsulation.None,
            host: {
                'aria-hidden': 'true',
            },
            animations: [
                trigger('fade', [
                    state('void', style({ opacity: 0 })),
                    state('* => *', style({ opacity: 1 })),
                    transition('* => *', animate(animationTimings)),
                ]),
                trigger('slideContent', [
                    state('in-top', style(animationTop)),
                    state('out-top', style(animationTop)),
                    state('in-bottom', style(animationBottom)),
                    state('out-bottom', style(animationBottom)),
                    state('in-right', style(animationRight)),
                    state('out-right', style(animationRight)),
                    state('in-left', style(animationLeft)),
                    state('out-left', style(animationLeft)),
                    transition('* => *', animate(animationTimings)),
                ]),
            ],
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-popover{background-color:#eceff1;border-radius:8px}.ln-popover.portrait{width:304px}.ln-popover.landscape{width:560px;display:flex}.ln-popover .ln-popover-content-container{padding:24px}.ln-popover .ln-popover-header{display:flex;flex-wrap:wrap;grid-gap:24px;margin-bottom:16px}.ln-popover .ln-popover-header .ln-popover-title{flex-grow:1}.ln-popover .ln-popover-header .ln-popover-title h4{font:bold 18px/24px Lato;margin:0}.ln-popover .ln-popover-body p{font:16px/24px Lato;margin-bottom:16px}.ln-popover .ln-popover-actions{display:block;text-align:right;padding-top:16px}.ln-popover .ln-popover-actions .ln-button+.ln-button{margin-left:16px}"]
        })
    ], Popover);
    return Popover;
}(PopoverBaseDirective));
/**
 * Image of popover
 */
var PopoverImage = /** @class */ (function () {
    function PopoverImage() {
    }
    PopoverImage = __decorate([
        Directive({
            selector: prefix + "-popover-image",
        })
    ], PopoverImage);
    return PopoverImage;
}());
/**
 * Title of popover
 */
var PopoverTitle = /** @class */ (function () {
    function PopoverTitle() {
    }
    PopoverTitle = __decorate([
        Component({
            selector: prefix + "-popover-title",
            template: '<h4><ng-content></ng-content></h4>',
            host: {
                class: prefix + "-popover-title",
            }
        })
    ], PopoverTitle);
    return PopoverTitle;
}());
/**
 * Header text container
 */
var PopoverHeader = /** @class */ (function () {
    function PopoverHeader() {
    }
    PopoverHeader = __decorate([
        Component({
            selector: prefix + "-popover-header",
            template: '<ng-content></ng-content>',
            host: {
                class: prefix + "-popover-header",
            }
        })
    ], PopoverHeader);
    return PopoverHeader;
}());
/**
 * Body of popover
 */
var PopoverBody = /** @class */ (function () {
    function PopoverBody() {
    }
    PopoverBody = __decorate([
        Component({
            selector: prefix + "-popover-body",
            template: '<ng-content></ng-content>',
            host: {
                class: prefix + "-popover-body",
            }
        })
    ], PopoverBody);
    return PopoverBody;
}());
/**
 * Container for popover action buttons
 */
var PopoverActions = /** @class */ (function () {
    function PopoverActions() {
    }
    PopoverActions = __decorate([
        Component({
            selector: prefix + "-popover-actions",
            template: '<ng-content></ng-content>',
            host: {
                class: prefix + "-popover-actions",
            }
        })
    ], PopoverActions);
    return PopoverActions;
}());

/** Injection token that determines the scroll handling while the menu is open. */
var POPOVER_SCROLL_STRATEGY = new InjectionToken('popover-scroll-strategy');
/**
 * Factory setting for the scroll strategy
 *
 * @param {Overlay}overlay Overlay-HTMLElement
 * @returns {ScrollStrategy} Strategy by which scroll-scenario will be handled
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function POPOVER_SCROLL_STRATEGY_FACTORY(overlay) {
    return function () { return overlay.scrollStrategies.reposition(); };
}
var POPOVER_SCROLL_STRATEGY_FACTORY_PROVIDER = {
    provide: POPOVER_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: POPOVER_SCROLL_STRATEGY_FACTORY,
};
// @dynamic
var PopoverDirective = /** @class */ (function () {
    function PopoverDirective(_overlayPositionBuilder, _elementRef, _overlay, _viewContainerRef, scrollStrategy) {
        this._overlayPositionBuilder = _overlayPositionBuilder;
        this._elementRef = _elementRef;
        this._overlay = _overlay;
        this._viewContainerRef = _viewContainerRef;
        this._scrollStrategy = scrollStrategy;
        this.ariaDescribedby = prefix + "-popover-" + Math.random().toString(36).substr(2, 5);
    }
    Object.defineProperty(PopoverDirective.prototype, "popover", {
        get: function () {
            return this._popover;
        },
        set: function (popover) {
            this._popover = popover;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Triggered when mouse enters or tab focus on the HTML Element
     */
    PopoverDirective.prototype.show = function () {
        if (this._popoverDisplayed) {
            return;
        }
        this.popover.id = this.ariaDescribedby;
        this._elementRef.nativeElement.setAttribute('aria-describedby', this.ariaDescribedby);
        this._getBehavior();
        this.popover.animationState = "in-" + this.popover.position;
        if (this._selectedBehavior !== 'click') {
            this._checkPopoverValidity();
            this._createOverlay();
            this._overlayRef.attach(this._getPortal());
            this._popoverDisplayed = true;
        }
    };
    /**
     * Triggered when mouse or focus leaves HTML Element
     */
    PopoverDirective.prototype.hideHover = function () {
        this.popover.animationState = "out-" + this.popover.position;
        if (this._selectedBehavior === 'hover') {
            this._overlayRef.detach();
            this._popoverDisplayed = false;
        }
    };
    /**
     * Triggered on escape key
     *
     */
    PopoverDirective.prototype.hide = function () {
        if (this._popoverDisplayed) {
            this._overlayRef.detach();
            this._popoverDisplayed = false;
        }
    };
    /**
     * check to see of the event target matches the trigger element, or close btn
     * if not, close the modal on click outside
     *
     *
     * @param {Event} event Triggered when HTML Element is clicked to open or close
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    PopoverDirective.prototype.onClick = function (event) {
        var overlayContainer = document.querySelector('.cdk-overlay-container');
        if (event.target === this._elementRef.nativeElement) {
            if (this._selectedBehavior !== 'hover') {
                this._popoverDisplayed = !this._popoverDisplayed;
                if (this._popoverDisplayed) {
                    this._checkPopoverValidity();
                    this._createOverlay();
                    this._overlayRef.attach(this._getPortal());
                    this._popoverDisplayed = true;
                    this._triggerFocus();
                }
                else {
                    this._overlayRef.detach();
                    this._popoverDisplayed = false;
                }
            }
        }
        else if (overlayContainer !== null && (event.target !== overlayContainer && !overlayContainer.contains(event.target))) {
            if (this._popoverDisplayed) {
                this._overlayRef.detach();
                this._popoverDisplayed = false;
            }
        }
    };
    // Throws error if popover with the given ID doesn't exist
    PopoverDirective.prototype._checkPopoverValidity = function () {
        if (!this.popover) {
            throw Error('matMenuTriggerFor: must pass in a popover instance.');
        }
    };
    // Creates an overlay keeping the orientation in mind
    PopoverDirective.prototype._createOverlay = function () {
        var originY = 'bottom';
        var overlayY = 'top';
        var originX = 'center';
        var overlayX = 'center';
        var offsetY = 2;
        var offsetX = 0;
        switch (this.popover.position) {
            case 'top': {
                originY = 'top';
                overlayY = 'bottom';
                originX = 'center';
                overlayX = 'center';
                offsetY = -2;
                offsetX = 0;
                break;
            }
            case 'left': {
                originY = 'center';
                overlayY = 'center';
                originX = 'start';
                overlayX = 'end';
                offsetY = 0;
                offsetX = -2;
                break;
            }
            case 'right': {
                originY = 'center';
                overlayY = 'center';
                originX = 'end';
                overlayX = 'start';
                offsetY = 0;
                offsetX = 2;
                break;
            }
            default: {
                break;
            }
        }
        if (!this._overlayRef || this.popover.position !== this._currentPosition) {
            var positionStrategy = this._overlayPositionBuilder
                .flexibleConnectedTo(this._elementRef)
                .withPositions([{
                    originX: originX,
                    originY: originY,
                    overlayX: overlayX,
                    overlayY: overlayY,
                    offsetY: offsetY,
                    offsetX: offsetX,
                }]);
            this._currentPosition = this.popover.position;
            this._overlayRef = this._overlay.create({ positionStrategy: positionStrategy, scrollStrategy: this._scrollStrategy() });
        }
        return this._overlayRef;
    };
    PopoverDirective.prototype._getPortal = function () {
        if (!this._portal || this._portal.templateRef !== this.popover.templateRef) {
            this._portal = new TemplatePortal(this.popover.templateRef, this._viewContainerRef);
        }
        return this._portal;
    };
    PopoverDirective.prototype._getBehavior = function () {
        this._selectedBehavior = this.popover.trigger;
    };
    PopoverDirective.prototype._triggerFocus = function () {
        var popover = document.querySelector("#" + this.popover.id);
        if (popover && popover !== null) {
            var focusable = popover.querySelectorAll('button, a');
            var firstFocusable = focusable[0];
            // set the focus in popover
            if (firstFocusable && firstFocusable !== null) {
                firstFocusable.setAttribute('tabindex', '0');
                firstFocusable.focus();
            }
        }
    };
    PopoverDirective.ctorParameters = function () { return [
        { type: OverlayPositionBuilder },
        { type: ElementRef },
        { type: Overlay },
        { type: ViewContainerRef },
        { type: undefined, decorators: [{ type: Inject, args: [POPOVER_SCROLL_STRATEGY,] }] }
    ]; };
    __decorate([
        Input()
    ], PopoverDirective.prototype, "popover", null);
    __decorate([
        HostListener('mouseenter'),
        HostListener('focus')
    ], PopoverDirective.prototype, "show", null);
    __decorate([
        HostListener('mouseleave'),
        HostListener('blur')
    ], PopoverDirective.prototype, "hideHover", null);
    __decorate([
        HostListener('document:keyup.escape')
    ], PopoverDirective.prototype, "hide", null);
    __decorate([
        HostListener('document:click', ['$event'])
    ], PopoverDirective.prototype, "onClick", null);
    PopoverDirective = __decorate([
        Directive({
            selector: '[popover]',
        }),
        __param(4, Inject(POPOVER_SCROLL_STRATEGY))
    ], PopoverDirective);
    return PopoverDirective;
}());

var PopoverModule = /** @class */ (function () {
    function PopoverModule() {
    }
    PopoverModule = __decorate([
        NgModule({
            imports: [
                CommonModule,
                OverlayModule,
            ],
            exports: [
                CdkScrollable,
                Popover,
                PopoverDirective,
                PopoverImage,
                PopoverTitle,
                PopoverHeader,
                PopoverBody,
                PopoverActions,
            ],
            declarations: [
                PopoverBaseDirective,
                Popover,
                PopoverDirective,
                PopoverImage,
                PopoverTitle,
                PopoverHeader,
                PopoverBody,
                PopoverActions,
            ],
            providers: [POPOVER_SCROLL_STRATEGY_FACTORY_PROVIDER],
        })
    ], PopoverModule);
    return PopoverModule;
}());

/*
 * Public API Surface of popover
 */

var Radio = /** @class */ (function () {
    function Radio() {
        /**
         * Whether the radio button is checked.
         * default false
         */
        this.checked = false;
        /**
         * Whether the radio button is disabled.
         * default false
         */
        this.disabled = false;
        /**
         * Event emitted when the radio button is selected.
         */
        this.change = new EventEmitter();
        this.prefix = prefix;
    }
    /**
     * @param {Event} event Stops propagation on the click event
     */
    Radio.prototype.onInputClick = function (event) {
        event.stopPropagation();
    };
    /**
     * Toggles the `checked` state of the radio
     *
     * @param {Event} event Stops propagation on the change event
     */
    Radio.prototype.onInputChange = function (event) {
        event.stopPropagation();
        this.checked = true;
        this.change.emit(event.target.value);
    };
    __decorate([
        Input()
    ], Radio.prototype, "name", void 0);
    __decorate([
        Input()
    ], Radio.prototype, "value", void 0);
    __decorate([
        Input()
    ], Radio.prototype, "checked", void 0);
    __decorate([
        Input(),
        HostBinding("class." + prefix + "-radio-disabled")
    ], Radio.prototype, "disabled", void 0);
    __decorate([
        Input('aria-label')
    ], Radio.prototype, "ariaLabel", void 0);
    __decorate([
        Input('aria-labelledby')
    ], Radio.prototype, "ariaLabelledby", void 0);
    __decorate([
        Input('aria-describedby')
    ], Radio.prototype, "ariaDescribedby", void 0);
    __decorate([
        Output()
    ], Radio.prototype, "change", void 0);
    Radio = __decorate([
        Component({
            selector: prefix + "-radio",
            template: "<label class=\"{{prefix}}-radio\">\n  <span class=\"{{prefix}}-radio-input\">\n    <input\n      type=\"radio\"\n      [name]=\"name\"\n      [value]=\"value\"\n      [checked]=\"checked\"\n      [disabled]=\"disabled\"\n      [attr.aria-label]=\"ariaLabel\"\n      [attr.aria-labelledby]=\"ariaLabelledby\"\n      [attr.aria-describedby]=\"ariaDescribedby\"\n      (change)=\"onInputChange($event)\"\n      (click)=\"onInputClick($event)\"\n    />\n    <span class=\"{{prefix}}-radio-outline\"></span>\n    <span class=\"{{prefix}}-radio-control\"></span>\n  </span>\n  <span class=\"{{prefix}}-radio-label\">\n    <ng-content></ng-content>\n  </span>\n</label>",
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}ln-radio{display:inline-flex}.ln-radio{display:inline-grid;display:-ms-inline-grid;grid-template-columns:min-content auto;-ms-grid-columns:min-content auto;cursor:pointer;align-items:center;grid-gap:16px}.ln-radio .ln-radio-control{width:20px;height:20px}.ln-radio .ln-radio-input input:focus~.ln-radio-outline,.ln-radio .ln-radio-input input:hover~.ln-radio-outline{height:40px;width:40px;top:-10px;left:-10px}.ln-radio .ln-radio-input input~.ln-radio-control::before{width:10px;height:10px;box-shadow:inset 10px 10px #0d47a1}.ln-radio>*{-ms-grid-row-align:center}.ln-radio-disabled .ln-radio{cursor:default;pointer-events:none}.ln-radio-control{display:grid;display:-ms-grid;place-items:center;border-radius:50%;border:2px solid #546e7a;box-sizing:border-box}.ln-radio-control>*{-ms-grid-column-align:center;-ms-grid-row-align:center}input~.ln-radio-control::before{content:\"\";width:10px;height:10px;box-shadow:inset 10px 10px #0d47a1;border-radius:50%;transition:transform 180ms ease-in-out;transform:scale(0)}input:checked~.ln-radio-control::before{transform:scale(1)}.ln-radio-small .ln-radio .ln-radio-control{width:16px;height:16px}.ln-radio-small .ln-radio .ln-radio-input input:focus~.ln-radio-outline,.ln-radio-small .ln-radio .ln-radio-input input:hover~.ln-radio-outline{height:32px;width:32px;top:-8px;left:-8px}.ln-radio-small .ln-radio .ln-radio-input input~.ln-radio-control::before{width:8px;height:8px;box-shadow:inset 10px 10px #0d47a1}.ln-radio-input{display:grid;display:-ms-grid;grid-template-areas:\"radio\";position:relative}.ln-radio-input>*{-ms-grid-row:1;-ms-grid-column:1;grid-area:radio}.ln-radio-input input{opacity:0;width:1px;height:1px}.ln-radio-input input:not([disabled]):focus~.ln-radio-outline,.ln-radio-input input:not([disabled]):hover~.ln-radio-outline{border-radius:50%;transition:opacity cubic-bezier(0,0,.2,1);position:absolute}.ln-radio-input input:not([disabled]):hover~.ln-radio-outline{background-color:rgba(0,0,0,.04)}.ln-radio-input input:not([disabled]):focus~.ln-radio-outline{background-color:rgba(0,0,0,.12)}.ln-radio-input input:not([disabled]):focus:checked~.ln-radio-outline{background-color:rgba(13,71,161,.12)}.ln-radio-input input:not([disabled]):hover:checked~.ln-radio-outline{background-color:rgba(13,71,161,.04)}.ln-radio-input input:checked~.ln-radio-control{border-color:#0d47a1}.ln-radio-input input:disabled~.ln-radio-control{opacity:.24}.ln-radio-input input:checked:disabled~.ln-radio-control::before{box-shadow:inset 10px 10px #546e7a}"]
        })
    ], Radio);
    return Radio;
}());

var RadioModule = /** @class */ (function () {
    function RadioModule() {
    }
    RadioModule = __decorate([
        NgModule({
            declarations: [Radio],
            imports: [CommonModule],
            exports: [Radio],
        })
    ], RadioModule);
    return RadioModule;
}());

/*
 * Public API Surface of radio
 */

// animation timings
var animationTimings$1 = '300ms cubic-bezier(0.645, 0.045, 0.355, 1)';
var animationBottom$1 = { transform: 'translate3d(0, -24px, 0)' };
var Snackbar = /** @class */ (function () {
    function Snackbar() {
        this.prefix = prefix;
        /**
         * Event emitted on click of action button
         */
        this.actionClicked = new EventEmitter();
    }
    Snackbar.prototype.onActionClick = function () {
        this.actionClicked.emit();
    };
    Snackbar = __decorate([
        Component({
            selector: prefix + "-snackbar",
            template: "<div class=\"{{prefix}}-snackbar-container\" role=\"alert\" aria-live=\"polite\" [@slideContent]=\"'bottom'\" @fade>\n  <span *ngIf=\"text\">{{ text }}</span>\n  <button *ngIf=\"actionLabel\" (click)=\"onActionClick()\">{{actionLabel}}</button>\n  <div class=\"circle\">\n    <svg class=\"circle__svg\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\">\n      <circle class=\"circle__svg-circle\" cx=\"50\" cy=\"50\" r=\"45\"/>\n    </svg>\n  </div>\n</div>",
            changeDetection: ChangeDetectionStrategy.OnPush,
            animations: [
                trigger('fade', [
                    state('void', style({ opacity: 0 })),
                    state('* => *', style({ opacity: 1 })),
                    transition('* => *', animate(animationTimings$1)),
                ]),
                trigger('slideContent', [
                    state('bottom', style(animationBottom$1)),
                    transition('* => *', animate(animationTimings$1)),
                ]),
            ],
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-snackbar-container{padding:16px 24px;border-radius:8px;font-size:16px;line-height:24px}.ln-snackbar-container span{padding-right:2px;color:#cfd8dc}.ln-snackbar-container button{background-color:transparent;padding:0 24px;color:#64b5f6;border-right:1px solid #485b7b;cursor:pointer}.ln-snackbar-container button:active,.ln-snackbar-container button:hover{color:#2196f3}.circle{height:24px;width:24px;display:inline-block;padding-left:24px;position:relative;vertical-align:sub}.circle__svg-circle{animation-timing-function:ease-in-out;animation-fill-mode:both;animation-name:circle__svg-circle;fill:transparent;stroke:#cad6e1;stroke-dasharray:282.743343;stroke-linecap:square;stroke-width:10;transform-origin:50% 50%}@keyframes circle__svg-circle{0%{stroke-dashoffset:0}100%{stroke-dashoffset:282.743343}}"]
        })
    ], Snackbar);
    return Snackbar;
}());

var SnackbarService = /** @class */ (function () {
    function SnackbarService(_overlay) {
        this._overlay = _overlay;
        /**
         * Event for action button click
         */
        this.actionClicked = new EventEmitter();
    }
    /**
     * Opens the snackbar with a simple string message
     *
     * @param {string} text Main content
     * @param {number} [duration=4] duration Time to live (in seconds)
     * @param {string=} actionLabel Label for action button
     */
    SnackbarService.prototype.show = function (text, duration, actionLabel) {
        var _this = this;
        if (duration === void 0) { duration = 4; }
        this.createOverlay();
        this.snackbarRef.instance.text = text;
        if (actionLabel && actionLabel.trim().length >= 1) {
            this.snackbarRef.instance.actionLabel = actionLabel.trim();
        }
        if (isNaN(duration) || duration < 4 || duration > 10) {
            duration = 4;
        }
        document.querySelector('.circle__svg-circle').style['animation-duration'] = duration + "s";
        this.snackbarRef.instance.actionClicked.subscribe(function () { return _this.actionClicked.emit(); });
        setTimeout(function () {
            _this.hide();
        }, duration * 1000);
    };
    /**
     * Dismisses the snackbar
     */
    SnackbarService.prototype.hide = function () {
        this._overlayRef.detach();
    };
    SnackbarService.prototype.createOverlay = function () {
        var positonStrategy = this._overlay.position().global();
        var overlayConfig = new OverlayConfig();
        positonStrategy.centerHorizontally();
        positonStrategy.bottom();
        this._overlayRef = this._overlayRef && this._overlayRef.detach();
        overlayConfig.positionStrategy = positonStrategy;
        this._overlayRef = this._overlay.create(overlayConfig);
        this.snackbarRef = this._overlayRef.attach(new ComponentPortal(Snackbar));
    };
    SnackbarService.ctorParameters = function () { return [
        { type: Overlay }
    ]; };
    SnackbarService = __decorate([
        Injectable()
    ], SnackbarService);
    return SnackbarService;
}());

var SnackbarModule = /** @class */ (function () {
    function SnackbarModule() {
    }
    SnackbarModule = __decorate([
        NgModule({
            imports: [
                CommonModule,
                OverlayModule,
                PortalModule,
            ],
            declarations: [Snackbar],
            providers: [SnackbarService],
            exports: [Snackbar],
            entryComponents: [Snackbar],
        })
    ], SnackbarModule);
    return SnackbarModule;
}());

/**
 * Public API Surface of snackbar
 */

var Switch = /** @class */ (function () {
    function Switch() {
        /**
         * whether or not a switch is checked
         * default false
         */
        this.checked = false;
        /**
         * whether or not a switch is disabled
         * default false
         */
        this.disabled = false;
        /**
         * Users can specify the `aria-labelledby` which will be forwarded to the input element.
         * default null
         */
        this.ariaLabelledby = null;
        /**
         * event emitted when a switch is toggled
         */
        this.toggle = new EventEmitter();
        this.prefix = prefix;
    }
    /**
     * @param {Event} event Stops propagation on the change event
     */
    Switch.prototype.onInteractionEvent = function (event) {
        event.stopPropagation();
    };
    /**
     * @param {Event} event Toggles the `checked` state of the switch
     */
    Switch.prototype.toggleSelection = function (event) {
        event.stopPropagation();
        if (this.disabled) {
            return;
        }
        this.checked = !this.checked;
        this.toggle.emit(this.checked);
    };
    __decorate([
        Input(),
        HostBinding("class." + prefix + "-checked")
    ], Switch.prototype, "checked", void 0);
    __decorate([
        Input(),
        HostBinding("class." + prefix + "-disabled")
    ], Switch.prototype, "disabled", void 0);
    __decorate([
        Input()
    ], Switch.prototype, "tabIndex", void 0);
    __decorate([
        Input('aria-labelledby')
    ], Switch.prototype, "ariaLabelledby", void 0);
    __decorate([
        Input('aria-describedby')
    ], Switch.prototype, "ariaDescribedby", void 0);
    __decorate([
        Output()
    ], Switch.prototype, "toggle", void 0);
    Switch = __decorate([
        Component({
            selector: prefix + "-switch",
            template: "<label>\n  <span class=\"{{prefix}}-switch-label\">\n    <ng-content></ng-content>\n  </span>\n  <span class=\"{{prefix}}-switch-slider\">\n  <input\n    type=\"checkbox\"\n    role=\"switch\"\n    [disabled]=\"disabled\"\n    [tabIndex]=\"tabIndex\"\n    [attr.aria-labelledby]=\"ariaLabelledby\"\n    [attr.aria-checked]=\"checked\"\n    [attr.aria-describedby]=\"ariaDescribedby\"\n    (change)=\"onInteractionEvent($event)\"\n    (click)=\"toggleSelection($event)\">\n  <span class=\"{{prefix}}-switch-outline\"></span>\n  <span class=\"{{prefix}}-switch-checkmark\"></span>\n</span>\n</label>",
            host: {
                'class': prefix + "-switch",
                '[attr.tabindex]': 'disabled ? null : -1',
            },
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-switch{display:inline-block}.ln-switch.ln-checked .ln-switch-checkmark,.ln-switch.ln-checked .ln-switch-outline{transform:translateX(14px);transition:transform 150ms ease-out 3ms}.ln-switch.ln-disabled{cursor:default;pointer-events:none}.ln-switch.ln-disabled .ln-switch-slider{opacity:.24}.ln-switch-label{display:block}.ln-switch-slider{display:inline-block;position:relative;cursor:pointer;width:34px;height:14px;border-radius:7px}.ln-switch-slider .ln-switch-checkmark{position:absolute;top:-3px;left:0;border-radius:50%;transition:transform 150ms ease-out 3ms;width:20px;height:20px;box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.ln-switch-slider:active .ln-switch-outline,.ln-switch-slider:hover .ln-switch-outline{border-radius:50%;width:40px;height:40px;position:absolute;top:-13px;left:-10px;transition:transform 150ms ease-out 3ms}.ln-switch-slider input{opacity:0;width:1px;height:1px}.ln-switch-slider input:focus~.ln-switch-outline{border-radius:50%;width:40px;height:40px;position:absolute;top:-13px;left:-10px;transition:transform 150ms ease-out 3ms}"]
        })
    ], Switch);
    return Switch;
}());

var SwitchModule = /** @class */ (function () {
    function SwitchModule() {
    }
    SwitchModule = __decorate([
        NgModule({
            declarations: [Switch],
            imports: [CommonModule],
            exports: [Switch],
        })
    ], SwitchModule);
    return SwitchModule;
}());

/*
 * Public API Surface of switch
 */

var Tab = /** @class */ (function () {
    function Tab() {
        /**
         * Whether the tab is disabled
         * Default set to false
         */
        this.disabled = false;
        /**
         * Event emitted when a tab is selected/unselected
         */
        this.selected = new EventEmitter();
        this.active = false;
    }
    __decorate([
        Input()
    ], Tab.prototype, "disabled", void 0);
    __decorate([
        Input()
    ], Tab.prototype, "title", void 0);
    __decorate([
        Output()
    ], Tab.prototype, "selected", void 0);
    Tab = __decorate([
        Component({
            selector: prefix + "-tab",
            template: "<div [class.tabs-hidden]=\"!active\" [id]=\"panelId\" role=\"tabpanel\" [attr.aria-labelledby]=\"headerId\">\n  <ng-content></ng-content>\n</div>\n"
        })
    ], Tab);
    return Tab;
}());

var Tabs = /** @class */ (function () {
    function Tabs(_elRef, renderer2) {
        this._elRef = _elRef;
        this.renderer2 = renderer2;
        /**
         * Tab orientation to use
         * default 'horizontal'
         */
        this.orientation = 'horizontal';
        /**
         * Whether to enable tab overflow
         * default true
         */
        this.overflow = true;
        /**
         * Event emitted when the tab selection has changed
         */
        this.change = new EventEmitter();
        this.prefix = prefix;
        this.tabsShow = [];
        this.tabsHideInMenu = [];
        this.isOverflowMenuOpen = false;
        this._totalTabsAvailableWidth = 0;
        this._selectedIndex = 0;
        this._DEFAULT_ID = "tabs_" + Math.random().toString(36).substr(2, 5);
        this._HEADER_ID = '-header-';
        this._PANEL_ID = '-panel-';
        this._currentScreenWidth = window.innerWidth;
    }
    Object.defineProperty(Tabs.prototype, "selectedIndex", {
        get: function () {
            return this._selectedIndex;
        },
        /**
         * Index of the active tab
         */
        set: function (index) {
            this._selectedIndex = index;
            this._changeSelectedIndexManually();
        },
        enumerable: true,
        configurable: true
    });
    Tabs.prototype.onResized = function () {
        if (this.orientation === 'horizontal' && this.overflow) {
            this._totalTabsAvailableWidth += window.innerWidth - this._currentScreenWidth;
            if (window.innerWidth <= this._currentScreenWidth) {
                this._currentScreenWidth = window.innerWidth;
                this._handleOverflow();
            }
            else {
                this._currentScreenWidth = window.innerWidth;
                this._handleOverflowOnIncreaseWidth();
            }
        }
    };
    Tabs.prototype.ngAfterViewInit = function () {
        var _this = this;
        if (this.tabs) {
            setTimeout(function () {
                _this._initializeTabIDs();
                _this._initializeTabs();
            }, 0);
            this._totalTabsAvailableWidth = this._elRef.nativeElement.querySelectorAll("." + prefix + "-tabs-headers")[0].offsetWidth;
        }
    };
    Tabs.prototype.activateTab = function (tab, index, inOverflowMenu) {
        if (this._currentSelectedTab === tab || tab.disabled) {
            return;
        }
        this._currentSelectedTab.selected.emit(false);
        this._currentSelectedTab.active = false;
        if (inOverflowMenu) {
            var removeElement = this.tabsShow.pop();
            this.tabsShow.push(tab);
            this.tabsHideInMenu.splice(index, 1);
            this.tabsHideInMenu.unshift(removeElement);
        }
        tab.active = true;
        this._currentSelectedTab = tab;
        this._currentSelectedTab.selected.emit(true);
        this.change.emit(tab.index);
    };
    Tabs.prototype.clickOnOverflow = function () {
        var _this = this;
        this.isOverflowMenuOpen = !this.isOverflowMenuOpen;
        setTimeout(function () {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            _this.isOverflowMenuOpen ? _this._trackFocus() : _this._destroyListener();
        }, 5);
    };
    Tabs.prototype._initializeTabs = function () {
        var _this = this;
        this.tabsShow = this.tabs.toArray();
        this._currentSelectedTab = this.tabsShow[this._selectedIndex];
        this._currentSelectedTab.active = true;
        if (this.orientation === 'horizontal' && this.overflow) {
            setTimeout(function () {
                _this._handleOverflow();
            }, 0);
        }
    };
    Tabs.prototype._changeSelectedIndexManually = function () {
        if (this.tabs && this._currentSelectedTab) {
            this._currentSelectedTab.active = false;
            this._currentSelectedTab = this.tabsShow[this._selectedIndex];
            this._currentSelectedTab.active = true;
        }
    };
    Tabs.prototype._handleOverflowOnIncreaseWidth = function () {
        var usedSpace = this._elRef.nativeElement.querySelectorAll("." + prefix + "-tabs-container")[0].offsetWidth;
        if (this.tabsHideInMenu.length > 0) {
            usedSpace += 50;
        }
        var tabs = this._elRef.nativeElement.querySelectorAll('.tab-hidden');
        var tabsToUnHide = 0;
        while (this._totalTabsAvailableWidth * 0.95 > usedSpace && tabsToUnHide < tabs.length - 1) {
            usedSpace = usedSpace + 250;
            tabsToUnHide += 1;
        }
        if (this._totalTabsAvailableWidth * 0.95 < usedSpace) {
            tabsToUnHide -= 1;
        }
        this.tabsShow = this.tabsShow.concat(this.tabsHideInMenu.slice(0, tabsToUnHide + 1));
        this.tabsHideInMenu = this.tabsHideInMenu.slice(tabsToUnHide + 1, this.tabsHideInMenu.length);
    };
    Tabs.prototype._handleOverflow = function () {
        var _a;
        var usedSpace = this._elRef.nativeElement.querySelectorAll("." + prefix + "-tabs-container")[0].offsetWidth;
        var tabsToHide = 0;
        var tabs = this._elRef.nativeElement.querySelectorAll('.tab-non-hidden');
        var indexToTarget;
        while ((this._totalTabsAvailableWidth - 50) < usedSpace) {
            indexToTarget = this.tabsShow.length - tabsToHide - 1;
            if (this.tabsShow[indexToTarget].active) {
                _a = __read([
                    this.tabsShow[indexToTarget - 1],
                    this.tabsShow[indexToTarget],
                ], 2), this.tabsShow[indexToTarget] = _a[0], this.tabsShow[indexToTarget - 1] = _a[1];
            }
            usedSpace = usedSpace - tabs[tabs.length - tabsToHide - 1].offsetWidth;
            tabsToHide += 1;
        }
        this.tabsHideInMenu = this.tabsShow.slice(this.tabsShow.length - tabsToHide, this.tabsShow.length).concat(this.tabsHideInMenu);
        this.tabsShow = this.tabsShow.slice(0, this.tabsShow.length - tabsToHide);
    };
    Tabs.prototype._initializeTabIDs = function () {
        var _this = this;
        if (!this.id) {
            this.id = this._DEFAULT_ID;
        }
        this.tabs.forEach(function (tab, i) {
            tab.headerId = "" + _this.id + _this._HEADER_ID + i;
            tab.panelId = "" + _this.id + _this._PANEL_ID + i;
            tab.index = i;
        });
    };
    Tabs.prototype._trackFocus = function () {
        var _this = this;
        this._clickObserver = this.renderer2.listen('window', 'click', function () {
            if (_this.isOverflowMenuOpen) {
                _this.clickOnOverflow();
            }
        });
    };
    Tabs.prototype._destroyListener = function () {
        if (this._clickObserver) {
            this._clickObserver();
        }
    };
    Tabs.ctorParameters = function () { return [
        { type: ElementRef },
        { type: Renderer2 }
    ]; };
    __decorate([
        Input(),
        HostBinding('id')
    ], Tabs.prototype, "id", void 0);
    __decorate([
        HostBinding('class'),
        Input()
    ], Tabs.prototype, "orientation", void 0);
    __decorate([
        Input()
    ], Tabs.prototype, "overflow", void 0);
    __decorate([
        Input()
    ], Tabs.prototype, "selectedIndex", null);
    __decorate([
        Output()
    ], Tabs.prototype, "change", void 0);
    __decorate([
        ContentChildren(Tab)
    ], Tabs.prototype, "tabs", void 0);
    __decorate([
        HostListener('window:resize', [])
    ], Tabs.prototype, "onResized", null);
    Tabs = __decorate([
        Component({
            selector: prefix + "-tabs",
            template: "<div role=\"tablist\" class=\"{{prefix}}-tabs-headers\" [ngClass]=\"orientation\" [class.no-overflow-tabs]=\"!overflow\">\n  <div class=\"{{prefix}}-tabs-container\" [class.tabs-non-overflow]=\"!overflow\">\n    <div *ngFor=\"let tab of tabsShow; let i = index\" class=\"{{prefix}}-tab-container tab-non-hidden\">\n      <button\n        class=\"non-overflow\"\n        role=\"tab\"\n        [id]=\"tab.headerId\"\n        (click)=\"activateTab(tab, i, false)\"\n        [disabled]=\"tab.disabled\"\n        [class.active]=\"tab.active\"\n        [attr.aria-disabled]=\"tab.disabled\"\n        [attr.aria-selected]=\"tab.active\"\n        [innerHTML]=\"tab.title\">\n      </button>\n      <div class=\"{{prefix}}-tabs-select-tab\" [class.selected]=\"tab.active\"></div>\n    </div>\n    <ng-container *ngIf=\"tabsHideInMenu.length > 0\">\n      <div class=\"{{prefix}}-tab-container drop-down-menu-icon\">\n        <button\n          (click)=\"clickOnOverflow()\"\n          class=\"non-overflow\"\n          [class.active]=\"isOverflowMenuOpen\"\n          [attr.aria-selected]=\"isOverflowMenuOpen\">\n          <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\"><defs><style>.a{fill:none;}.b{fill:#546e7a;}</style></defs><g transform=\"translate(-324 -391)\"><g transform=\"translate(73 -58)\"><rect class=\"a\" width=\"24\" height=\"24\" transform=\"translate(251 449)\"/><g transform=\"translate(251 449)\"><path class=\"b\" d=\"M6,10a2,2,0,1,0,2,2A2.006,2.006,0,0,0,6,10Zm12,0a2,2,0,1,0,2,2A2.006,2.006,0,0,0,18,10Zm-6,0a2,2,0,1,0,2,2A2.006,2.006,0,0,0,12,10Z\"/></g></g></g></svg>\n        </button>\n        <div [class.tabs-hidden]=\"!isOverflowMenuOpen\" [ngClass]=\"[prefix] + '-tabs-overflow-menu'\">\n          <div *ngFor=\"let tab of tabsHideInMenu; let i = index\" (click)=\"activateTab(tab, i, true)\" class=\"{{prefix}}-tab-container  tab-hidden\">\n            <button\n              class=\"non-overflow\"\n              role=\"tab\"\n              [id]=\"tab.headerId\"\n              [disabled]=\"tab.disabled\"\n              [attr.aria-disabled]=\"tab.disabled\"\n              [innerHTML]=\"tab.title\">\n            </button>\n          </div>\n        </div>\n      </div>\n    </ng-container>\n  </div>\n</div>\n<div class=\"{{prefix}}-tabs-content\" [ngClass]=\"orientation\">\n  <ng-content></ng-content>\n</div>",
            host: {
                class: prefix + "-tabs",
            },
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}ln-tabs{display:flex;flex-direction:column}ln-tabs.vertical{flex-direction:row}ln-tabs .tabs-hidden{display:none}ln-tabs .ln-tabs-headers{border-bottom:2px solid #acbac9;display:flex;flex-direction:row;width:-moz-fit-content;width:fit-content;position:relative;height:56px}ln-tabs .ln-tabs-headers.no-overflow-tabs{height:auto}ln-tabs .ln-tabs-headers .ln-tabs-container{position:absolute;display:flex;width:-moz-max-content;width:max-content}ln-tabs .ln-tabs-headers .ln-tabs-container.tabs-non-overflow{width:auto;flex-wrap:wrap;position:static}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container.drop-down-menu-icon{position:relative}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container button{cursor:pointer;padding:16px 0;color:#546e7a;font-size:16px;background-color:inherit;position:relative;font-weight:600}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container button.active{font-weight:700;text-decoration:none;color:#00172e}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container button:focus,ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container button:hover{color:#37474f}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container button:disabled{pointer-events:none;cursor:default;opacity:.5}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container .ln-tabs-select-tab{position:relative;height:4px;color:inherit;bottom:-2px}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container .ln-tabs-select-tab.selected{background-color:#f6271c}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container:not(:last-child) button{margin:0 32px 0 0}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tab-container:not(:last-child) .ln-tabs-select-tab{width:calc(100% - 32px)}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tabs-overflow-menu{width:224px;box-shadow:0 1px 1px rgba(0,0,0,.14);border-radius:8px;background-color:#fff;position:absolute;right:0;top:72px;padding:8px}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tabs-overflow-menu .ln-tab-container{height:56px;cursor:pointer}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tabs-overflow-menu .ln-tab-container:hover{background-color:#fafafa}ln-tabs .ln-tabs-headers .ln-tabs-container .ln-tabs-overflow-menu .ln-tab-container button{padding-left:16px;margin:0}ln-tabs .ln-tabs-headers.vertical{flex-direction:column;border:none;border-left:2px solid #acbac9;height:auto}ln-tabs .ln-tabs-headers.vertical .ln-tabs-container{flex-direction:column;position:static}ln-tabs .ln-tabs-headers.vertical .ln-tabs-container .ln-tab-container{flex-direction:row-reverse;justify-content:flex-end;display:flex}ln-tabs .ln-tabs-headers.vertical .ln-tabs-container .ln-tab-container button{padding:16px}ln-tabs .ln-tabs-headers.vertical .ln-tabs-container .ln-tab-container .ln-tabs-select-tab{height:49px;width:4px;left:-2px}ln-tabs .ln-tabs-headers.vertical .ln-tabs-container .ln-tab-container:not(:last-child) button{margin:0 0 8px}ln-tabs .ln-tabs-headers.horizontal{width:100%}ln-tabs .ln-tabs-content{padding:32px}"]
        })
    ], Tabs);
    return Tabs;
}());

var TabsModule = /** @class */ (function () {
    function TabsModule() {
    }
    TabsModule = __decorate([
        NgModule({
            declarations: [
                Tab,
                Tabs,
            ],
            imports: [CommonModule],
            exports: [
                Tab,
                Tabs,
            ],
        })
    ], TabsModule);
    return TabsModule;
}());

/*
 * Public API Surface of tabs
 */

var BACKSPACE = 8;
var SPACE = 32;
var DELETE = 46;
var Tag = /** @class */ (function () {
    function Tag() {
        /**
         * Whether or not the tag is removable and emits remove event
         * default false
         */
        this.removable = false;
        /**
         * Whether or not the tag is selectable
         * default false
         */
        this.selectable = false;
        /**
         * Whether the tag is disabled
         * default false
         */
        this.disabled = false;
        /**
         * Whether the tag is selected
         * default false
         */
        this.selected = false;
        /**
         * Event emitted when the tag is to be removed
         */
        this.removed = new EventEmitter();
        /**
         * Event emitted when the tag is checked or unchecked
         */
        this.selectionChanged = new EventEmitter();
    }
    // readonly prefix = prefix;
    Tag.prototype._handleKeydown = function (event) {
        if (this.disabled) {
            return;
        }
        switch (event.keyCode) {
            case DELETE:
            case BACKSPACE:
                if (this.removable) {
                    this.remove();
                }
                event.preventDefault();
                break;
            case SPACE:
                if (this.selectable) {
                    this.toggleSelection();
                }
                event.preventDefault();
                break;
        }
    };
    /**
     * Inform listener of the removal request
     */
    Tag.prototype.remove = function () {
        if (this.disabled) {
            return;
        }
        this.removed.emit();
    };
    /**
     * Toggle tag selection state
     */
    Tag.prototype.toggleSelection = function () {
        if (this.disabled) {
            return;
        }
        this.selected = !this.selected;
        this.selectionChanged.emit(this.selected);
    };
    /*
     * Handles click events on the tag.
     */
    Tag.prototype._handleClick = function (event) {
        if (this.disabled) {
            event.preventDefault();
        }
        else {
            event.stopPropagation();
        }
    };
    __decorate([
        Input(),
        HostBinding("class." + prefix + "-tag-removable")
    ], Tag.prototype, "removable", void 0);
    __decorate([
        Input(),
        HostBinding("class." + prefix + "-tag-selectable")
    ], Tag.prototype, "selectable", void 0);
    __decorate([
        Input()
    ], Tag.prototype, "disabled", void 0);
    __decorate([
        Input()
    ], Tag.prototype, "selected", void 0);
    __decorate([
        Output()
    ], Tag.prototype, "removed", void 0);
    __decorate([
        Output()
    ], Tag.prototype, "selectionChanged", void 0);
    Tag = __decorate([
        Component({
            selector: prefix + "-tag",
            template: "\n  <" + prefix + "-checkbox\n  *ngIf=\"selectable\"\n  [checked]=\"selected\"\n  [disabled]=\"disabled\"\n  (change)=\"toggleSelection()\"\n  class=\"" + prefix + "-checkbox-round\"\n  tabIndex=\"-1\">\n  <ng-container *ngTemplateOutlet=\"label\"></ng-container>\n</" + prefix + "-checkbox>\n<ng-container *ngIf=\"!selectable\">\n  <ng-container *ngTemplateOutlet=\"label\"></ng-container>\n</ng-container>\n<ng-template #label>\n  <ng-content></ng-content>\n</ng-template>\n<span *ngIf=\"removable\" class=\"" + prefix + "-tag-remove\" (click)=\"remove()\" role=\"img\">\n  <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\">\n    <g transform=\"translate(-1080 -96)\">\n      <g transform=\"translate(1084.5 100.5)\">\n        <line class=\"b\" stroke=\"currentColor\" x1=\"10\" y2=\"10\" transform=\"translate(2.5 2.5)\"/>\n        <line class=\"b\" stroke=\"currentColor\" x2=\"10\" y2=\"10\" transform=\"translate(2.5 2.5)\"/>\n      </g>\n    </g>\n  </svg>\n</span>",
            encapsulation: ViewEncapsulation.None,
            host: {
                'class': prefix + "-tag",
                '[attr.disabled]': 'disabled ? disabled : null',
                '(keydown)': '_handleKeydown($event)',
                '(click)': '_handleClick($event)',
                '[attr.tabindex]': 'disabled ? null : 0',
                '[tabIndex]': '!removable && !selectable ? -1 : 0',
            },
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-tag{display:inline-flex;box-sizing:border-box;cursor:default;overflow:hidden;align-items:center;background-color:#eceff1;color:#000;transition:box-shadow 150ms;font-size:16px;border-radius:20px;height:40px;padding:8px 16px}.ln-tag.ln-tag-selectable{padding-left:8px}.ln-tag.ln-tag-removable{padding-right:8px}.ln-tag.ln-tag-removable .ln-tag-remove{margin-left:8px;display:flex}.ln-tag ln-checkbox{display:flex}.ln-tag .ln-checkbox-round input:focus~.ln-checkbox-outline,.ln-tag .ln-checkbox-round input:hover~.ln-checkbox-outline{background-color:transparent}.ln-tag.ln-tag-removable:not([disabled]) .ln-tag-remove{cursor:pointer}.ln-tag.ln-tag-removable:not([disabled]):focus,.ln-tag.ln-tag-selectable:not([disabled]):focus{outline:0;background-color:#fff;color:#000;box-shadow:0 0 0 3px #4fc3f7}.ln-tag.ln-tag-removable:not([disabled]):hover,.ln-tag.ln-tag-selectable:not([disabled]):hover{background-color:#cfd8dc;color:#000}.ln-tag[disabled]{opacity:.5;background-color:#eceff1;color:#000;box-shadow:none}.ln-tags-wrapper{display:flex;width:100%;border:0;flex-direction:row;flex-wrap:wrap}.ln-tags-wrapper .ln-tag{margin:4px}.ln-tags-wrapper.ln-tags-small .ln-tag{font-size:14px;border-radius:16px;height:32px;padding:7px 16px 8px}.ln-tags-wrapper.ln-tags-small .ln-tag.ln-tag-selectable{padding-left:8px}.ln-tags-wrapper.ln-tags-small .ln-tag.ln-tag-removable{padding-right:8px}.ln-tags-wrapper.ln-tags-small .ln-tag.ln-tag-removable .ln-tag-remove{margin-left:8px}.ln-tags-wrapper.ln-tags-small .ln-tag ln-checkbox{display:flex}"]
        })
    ], Tag);
    return Tag;
}());

var Tags = /** @class */ (function () {
    function Tags() {
        /**
         * size of the tag: small, large
         * Default 'large'
         */
        this.size = 'large';
    }
    __decorate([
        Input()
    ], Tags.prototype, "size", void 0);
    Tags = __decorate([
        Component({
            selector: prefix + "-tags",
            template: "\n  <div class=\"" + prefix + "-tags-wrapper\" [ngClass]=\"size === 'small' ? '" + prefix + "-tags-small " + prefix + "-checkbox-round-small' : ''\">\n    <ng-content select=\"" + prefix + "-tag\"></ng-content>\n  </div>",
            encapsulation: ViewEncapsulation.None
        })
    ], Tags);
    return Tags;
}());

var TagsModule = /** @class */ (function () {
    function TagsModule() {
    }
    TagsModule = __decorate([
        NgModule({
            declarations: [
                Tag,
                Tags,
            ],
            imports: [
                CommonModule,
                CheckboxModule,
            ],
            exports: [
                Tag,
                Tags,
            ],
        })
    ], TagsModule);
    return TagsModule;
}());

/*
 * Public API Surface of tags
 */

/*
 * Public API Surface of tooltip
 */

var FormElement = /** @class */ (function () {
    function FormElement() {
    }
    FormElement = __decorate([
        Component({
            selector: prefix + "-form-element, [" + prefix + "-form-element]",
            template: '<ng-content></ng-content>',
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-form-error-text,.ln-form-help-text,.ln-form-label{color:#455a64;display:flex;flex-direction:row;justify-content:space-between;margin-bottom:16px}.ln-form-label{font:700 16px/24px Lato}.ln-form-label .error{color:#d60019}.ln-form-error-text,.ln-form-help-text{font:400 14px/24px Lato}.ln-form-error-text{color:#d60019;justify-content:left;line-height:17px}.ln-form-error-text span{padding-left:8px}"]
        })
    ], FormElement);
    return FormElement;
}());
var FormLabel = /** @class */ (function () {
    function FormLabel() {
    }
    FormLabel = __decorate([
        Component({
            selector: prefix + "-form-label",
            template: '<label><ng-content></ng-content></label>',
            host: {
                class: prefix + "-form-label",
            }
        })
    ], FormLabel);
    return FormLabel;
}());
var FormHelpText = /** @class */ (function () {
    function FormHelpText() {
    }
    FormHelpText = __decorate([
        Component({
            selector: prefix + "-form-help-text",
            template: '<span><ng-content></ng-content></span>',
            host: {
                class: prefix + "-form-help-text",
            }
        })
    ], FormHelpText);
    return FormHelpText;
}());
var FormErrorText = /** @class */ (function () {
    function FormErrorText() {
    }
    FormErrorText = __decorate([
        Component({
            selector: prefix + "-form-error-text",
            template: "\n    <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" viewBox=\"0 0 16 16\"><defs><style>.a{fill:#d60019;}</style></defs><path class=\"a\" d=\"M11.5,3.5a8,8,0,1,0,8,8A7.957,7.957,0,0,0,11.5,3.5Zm1.113,13.426a1.534,1.534,0,0,1-1.043.417,1.706,1.706,0,0,1-1.113-.417,1.48,1.48,0,0,1-.487-1.043,1.252,1.252,0,0,1,.487-1.043,1.58,1.58,0,0,1,1.113-.417,1.416,1.416,0,0,1,1.043.417,1.361,1.361,0,0,1,0,2.087Zm.348-8.974c-.07.348-.974,4.522-1.113,5.5h-.626c-.139-1.043-1.043-5.078-1.113-5.426a7.307,7.307,0,0,1-.139-.9,1.252,1.252,0,0,1,.487-1.043,1.58,1.58,0,0,1,1.113-.417,1.706,1.706,0,0,1,1.113.417,1.48,1.48,0,0,1,.487,1.043A2.432,2.432,0,0,0,12.961,7.952Z\" transform=\"translate(-3.5 -3.5)\"/></svg>\n  <span><ng-content></ng-content></span>\n",
            host: {
                class: prefix + "-form-error-text",
            }
        })
    ], FormErrorText);
    return FormErrorText;
}());

var SelectService = /** @class */ (function () {
    function SelectService() {
        this.selectedItem = new BehaviorSubject('Select an option');
        this.currentselectedItem = this.selectedItem.asObservable();
        this.toggle = new BehaviorSubject(false);
        this.currentToggleState = this.toggle.asObservable();
    }
    SelectService.prototype.changeSelectedItem = function (item) {
        this.selectedItem.next(item);
    };
    SelectService.prototype.changeToggleState = function (state) {
        this.toggle.next(state);
    };
    SelectService = __decorate([
        Injectable()
    ], SelectService);
    return SelectService;
}());

var Select = /** @class */ (function () {
    function Select(_selectService) {
        this._selectService = _selectService;
        /**
         * whether or not the menu is disabled
         * default false
         */
        this.disabled = false;
        // TODO add aria-labelledby input
        // TODO
        /**
         * whether or not the form element is valid
         * default true
         */
        this.valid = true;
        /**
         * Set the name of the form field for validation and value retrieval
         */
        this.fieldName = 'selectmenu';
        /**
         * whether or not the dropdown is open
         * default false
         */
        this.open = false;
    }
    Select.prototype.ngOnInit = function () {
        var _this = this;
        this._selectService.currentselectedItem.subscribe(function (item) { return _this.selectedItem = item; });
        this._selectService.currentToggleState.subscribe(function (toggle) { return _this.open = toggle; });
    };
    /**
     * Toggles the content open or closed.
     */
    Select.prototype.toggle = function () {
        this.open = !this.open;
    };
    Select.ctorParameters = function () { return [
        { type: SelectService }
    ]; };
    __decorate([
        Input()
    ], Select.prototype, "disabled", void 0);
    __decorate([
        Input()
    ], Select.prototype, "valid", void 0);
    __decorate([
        Input()
    ], Select.prototype, "fieldName", void 0);
    Select = __decorate([
        Component({
            selector: prefix + "-select",
            template: "<div class=\"select-box\">\n      <button class=\"" + prefix + "-input select-toggle\" type=\"button\" role=\"combobox\" [class.open]=\"open\"\n      aria-controls=\"listbox\" aria-haspopup=\"listboxdropdown\" attr.aria-expanded=\"{{ open }}\"\n      (click)=\"toggle()\" [disabled]=\"disabled\" [class.error]=\"!valid\">\n        <span class=\"sortbymenulabel\">{{ selectedItem }}</span>\n        <input type=\"text\" [(ngModel)]=\"selectedItem\" class=\"hidden\" placeholder=\"{{ selectedItem }}\" ngModel id=\"{{ fieldName }}\" name=\"{{ fieldName }}\" />\n        <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\">\n        <g transform=\"translate(13060 14894)\" *ngIf=\"!open\">\n          <g transform=\"translate(-13060 -14894)\">\n            <path class=\"select-arrow-b\" d=\"M16,10l-4,4L8,10\"/>\n          </g>\n        </g>\n        <g transform=\"translate(8 10)\" *ngIf=\"open\">\n          <path class=\"select-arrow-b\" d=\"M16,14l-4-4L8,14\" transform=\"translate(-8 -10)\"/>\n        </g>\n      </svg>\n      </button>\n      <div class=\"select-content\" *ngIf=\"open\" role=\"listboxdropdown\" id=\"listbox\">\n          <ng-content></ng-content>\n      </div>\n    </div>",
            encapsulation: ViewEncapsulation.None,
            host: {
                class: prefix + "-select",
            },
            providers: [SelectService],
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-select .select-box{position:relative;width:-moz-max-content;width:max-content;margin-bottom:16px}.ln-select .select-box .select-toggle{background-color:#fff;display:flex;justify-content:space-between;margin-bottom:4px}.ln-select .select-box .select-toggle.open{border-color:#4fc3f7;box-shadow:0 0 0 4px #4fc3F729}.ln-select .select-box .select-toggle[disabled]{background-color:rgba(236,239,241,.5);color:#acbac9}.ln-select .select-box .select-toggle[disabled] .select-arrow-b{stroke:#acbac9}.ln-select .select-box .select-toggle .hidden{position:absolute;height:1px;width:1px;overflow:hidden;clip:rect(1px,1px,1px,1px)}.ln-select .select-box .select-content{background-color:#fff;border:2px solid #aebac8;border-radius:4px;box-sizing:border-box;position:absolute;z-index:1;width:100%}.ln-select .select-box .select-content button{background-color:#fff;display:flex;height:48px;justify-content:space-between;padding:12px 16px;text-align:left;width:100%}.ln-select .select-box .select-content button:hover{background-color:#f5f5f5}.ln-select .select-box .select-content button:active{background-color:#eee}.ln-select .select-box .select-content button:focus{background-color:#f5f5f5;outline:#4fc3f7 solid 2px}.ln-select .select-box .select-content button[disabled]{color:#00172e;opacity:.24}.ln-select .select-box .checkmark-b,.ln-select .select-box .select-arrow-b{fill:none;stroke:#00172e;stroke-linecap:round;stroke-linejoin:round;stroke-width:2px}", ".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-input{min-width:320px;height:48px;padding:12px 16px;border:2px solid #acbac9;border-radius:4px;box-sizing:border-box;font-size:16px;color:#00172e;-webkit-appearance:none;-moz-appearance:none;appearance:none;margin-bottom:16px}.ln-input:hover{border-color:#687c96}.ln-input:focus{border-color:#4fc3f7;box-shadow:0 0 0 4px #4fc3F729;outline:0}.ln-input.error{border-color:#d60019;box-shadow:0 0 0 2px #d6001929}.ln-input[disabled]{border-color:#eceff1;background-color:rgba(236,239,241,.5)}.ln-input ::-moz-placeholder{opacity:1}.ln-input :-ms-input-placeholder{opacity:1}.ln-input ::placeholder{opacity:1}"]
        })
    ], Select);
    return Select;
}());
var SelectOption = /** @class */ (function () {
    function SelectOption(_selectService) {
        this._selectService = _selectService;
        /**
         * whether or not the option is disabled
         * default false
         */
        this.disabled = false;
    }
    SelectOption.prototype.onOptionSelect = function (value) {
        this._selectService.changeSelectedItem(value);
        this._selectService.changeToggleState(false);
    };
    SelectOption.ctorParameters = function () { return [
        { type: SelectService }
    ]; };
    __decorate([
        Input()
    ], SelectOption.prototype, "value", void 0);
    __decorate([
        Input()
    ], SelectOption.prototype, "selected", void 0);
    __decorate([
        Input()
    ], SelectOption.prototype, "disabled", void 0);
    SelectOption = __decorate([
        Component({
            selector: prefix + "-select-option, [" + prefix + "-select-option]",
            template: "<button type=\"button\" role=\"option\" attr.aria-selected=\"{{ selected }}\" (click)=\"onOptionSelect(value)\" [disabled]=\"disabled\">\n  <ng-content></ng-content>\n    <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" *ngIf=\"selected\">\n      <g transform=\"translate(-1226 -513)\">\n        <g transform=\"translate(1226 512)\"><path class=\"checkmark-b\" d=\"M2,12l7,7L22,6\"/></g>\n      </g>\n    </svg>\n  </button>",
            encapsulation: ViewEncapsulation.None,
            host: {
                class: prefix + "-select-option",
            },
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-select .select-box{position:relative;width:-moz-max-content;width:max-content;margin-bottom:16px}.ln-select .select-box .select-toggle{background-color:#fff;display:flex;justify-content:space-between;margin-bottom:4px}.ln-select .select-box .select-toggle.open{border-color:#4fc3f7;box-shadow:0 0 0 4px #4fc3F729}.ln-select .select-box .select-toggle[disabled]{background-color:rgba(236,239,241,.5);color:#acbac9}.ln-select .select-box .select-toggle[disabled] .select-arrow-b{stroke:#acbac9}.ln-select .select-box .select-toggle .hidden{position:absolute;height:1px;width:1px;overflow:hidden;clip:rect(1px,1px,1px,1px)}.ln-select .select-box .select-content{background-color:#fff;border:2px solid #aebac8;border-radius:4px;box-sizing:border-box;position:absolute;z-index:1;width:100%}.ln-select .select-box .select-content button{background-color:#fff;display:flex;height:48px;justify-content:space-between;padding:12px 16px;text-align:left;width:100%}.ln-select .select-box .select-content button:hover{background-color:#f5f5f5}.ln-select .select-box .select-content button:active{background-color:#eee}.ln-select .select-box .select-content button:focus{background-color:#f5f5f5;outline:#4fc3f7 solid 2px}.ln-select .select-box .select-content button[disabled]{color:#00172e;opacity:.24}.ln-select .select-box .checkmark-b,.ln-select .select-box .select-arrow-b{fill:none;stroke:#00172e;stroke-linecap:round;stroke-linejoin:round;stroke-width:2px}"]
        })
    ], SelectOption);
    return SelectOption;
}());

var InputField = /** @class */ (function () {
    function InputField() {
        /**
         * whether or not input field disabled
         * default false
         */
        this.disabled = false;
        // /**
        //  * whether or not input field required
        //  * default false
        //  */
        // @Input()
        // required = false;
        // TODO
        /**
         * whether or not the form element is valid
         * default true
         */
        this.valid = true;
    }
    __decorate([
        Input()
    ], InputField.prototype, "disabled", void 0);
    __decorate([
        Input()
    ], InputField.prototype, "valid", void 0);
    InputField = __decorate([
        Component({
            selector: prefix + "-input",
            template: "\n  <input type=\"text\" class=\"" + prefix + "-input\" [class.error]=\"!valid\"\n  [disabled]=\"disabled\"/>\n  ",
            encapsulation: ViewEncapsulation.None,
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}.ln-input{min-width:320px;height:48px;padding:12px 16px;border:2px solid #acbac9;border-radius:4px;box-sizing:border-box;font-size:16px;color:#00172e;-webkit-appearance:none;-moz-appearance:none;appearance:none;margin-bottom:16px}.ln-input:hover{border-color:#687c96}.ln-input:focus{border-color:#4fc3f7;box-shadow:0 0 0 4px #4fc3F729;outline:0}.ln-input.error{border-color:#d60019;box-shadow:0 0 0 2px #d6001929}.ln-input[disabled]{border-color:#eceff1;background-color:rgba(236,239,241,.5)}.ln-input ::-moz-placeholder{opacity:1}.ln-input :-ms-input-placeholder{opacity:1}.ln-input ::placeholder{opacity:1}"]
        })
    ], InputField);
    return InputField;
}());
// TODO add aria-labelledby input

var PasswordField = /** @class */ (function () {
    function PasswordField() {
        /**
         * whether or not the form element is valid
         * default true
         */
        this.valid = true;
        /**
         * show password type
         */
        this.passwordType = 'password';
        /**
         * show password
         */
        this.showPassword = false;
        /**
         * value
         */
        this.value = '';
        /**
         * whether or not input field disabled
         * default false
         */
        this.disabled = false;
        this.showText = 'Show';
        this.hideText = 'Hide';
        this.srPasswordText = 'Password hidden';
    }
    PasswordField.prototype.togglePasswordMask = function () {
        this.showPassword = !this.showPassword;
        this.passwordType = this.showPassword ? 'text' : 'password';
        // text for screen reader only
        this.srPasswordText = this.showPassword ? 'Password shown' : 'Password hidden';
    };
    __decorate([
        Input()
    ], PasswordField.prototype, "valid", void 0);
    __decorate([
        Input()
    ], PasswordField.prototype, "showPasswordIcon", void 0);
    __decorate([
        Input()
    ], PasswordField.prototype, "passwordType", void 0);
    __decorate([
        Input()
    ], PasswordField.prototype, "showPassword", void 0);
    __decorate([
        Input()
    ], PasswordField.prototype, "value", void 0);
    __decorate([
        Input()
    ], PasswordField.prototype, "disabled", void 0);
    PasswordField = __decorate([
        Component({
            selector: prefix + "-password",
            template: "<div class=\"lng-form-field lng-input-field\">\n  <p aria-live=\"polite\" class=\"sr-only\">{{ srPasswordText }}</p>\n  <button type=\"button\" class=\"password-toggle\" *ngIf=\"showPasswordIcon\"\n  [attr.aria-pressed]=\"showPassword\" aria-label=\"toggle password visibility\"\n    (click)=\"togglePasswordMask()\">\n    <ng-container *ngIf=\"!showPassword; else hidePassword\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\">\n        <g transform=\"translate(0 -2)\">\n          <path\n            d=\"M8,14c4.707,0,7.744-5.284,7.871-5.508a1,1,0,0,0,0-.98C15.746,7.287,12.731,2,8,2,3.245,2,\n            .251,7.289.126,7.514a1,1,0,0,0,0,.975C.254,8.713,3.269,14,8,14ZM8,4c2.839,\n            0,5.036,2.835,5.818,4C13.034,9.166,10.837,12,8,12S2.962,9.162,2.181,8C2.958,6.835,5.146,4,8,4Z\" />\n          <circle cx=\"2\" cy=\"2\" r=\"2\" transform=\"translate(6 6)\" />\n        </g>\n      </svg>\n      <span aria-hidden=\"true\">{{showText}}</span>\n      <span aria-live=\"polite\" class=\"sr-only\">{{showText}}</span>\n    </ng-container>\n    <ng-template #hidePassword>\n      <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"hide-icon\">\n        <g transform=\"translate(0 0)\">\n          <path d=\"M14.574,5.669,13.15,7.093c.278.347.506.664.669.907-.76,1.13-2.85,\n          3.82-5.561,3.985L6.443,13.8A6.392,6.392,0,0,0,8,14c4.707,0,7.744-5.284,7.871-5.508a1,\n          1,0,0,0,0-.98A13.292,13.292,0,0,0,14.574,5.669Z\" />\n          <path d=\"M.293,15.707a1,1,0,0,0,1.414,0l14-14A1,1,0,0,0,14.293.293L11.548,\n          3.038A6.958,6.958,0,0,0,8,2C3.245,2,.251,7.289.126,7.514a1,1,0,0,0,0,.975,14.438,\n          14.438,0,0,0,2.693,3.276L.292,14.294A1,1,0,0,0,.293,15.707ZM2.181,8C2.958,6.835,5.146,4,8,4a4.794,\n          4.794,0,0,1,2.078.508L8.512,6.074A1.925,1.925,0,0,0,8,6,2,2,0,0,0,6,8a1.925,1.925,0,0,0,\n          .074.512L4.24,10.346A12.6,12.6,0,0,1,2.181,8Z\" />\n        </g>\n      </svg>\n      <span aria-hidden=\"true\">{{hideText}}</span>\n      <span aria-live=\"polite\" class=\"sr-only\">{{hideText}}</span>\n    </ng-template>\n  </button>\n  <input #password id=\"password\" [value]=\"value\" [type]=\"passwordType\" class=\"" + prefix + "-input password\" [disabled]=\"disabled\"\n    [class.error]=\"!valid\" />\n  </div>\n  ",
            encapsulation: ViewEncapsulation.None,
            host: {
                class: prefix + "-password",
            },
            styles: [".elevation-z0{box-shadow:none}.elevation-z1{box-shadow:0 1px 1px 0 rgba(0,0,0,.14),0 2px 1px -1px rgba(0,0,0,.12),0 1px 3px 0 rgba(0,0,0,.2)}.elevation-z2{box-shadow:0 2px 2px 0 rgba(0,0,0,.14),0 3px 1px -2px rgba(0,0,0,.12),0 1px 5px 0 rgba(0,0,0,.2)}.elevation-z3{box-shadow:0 3px 4px 0 rgba(0,0,0,.14),0 3px 3px -2px rgba(0,0,0,.12),0 1px 8px 0 rgba(0,0,0,.2)}.elevation-z4{box-shadow:0 4px 5px 0 rgba(0,0,0,.14),0 1px 10px 0 rgba(0,0,0,.12),0 2px 4px -1px rgba(0,0,0,.2)}.elevation-z6{box-shadow:0 6px 10px 0 rgba(0,0,0,.14),0 1px 18px 0 rgba(0,0,0,.12),0 3px 5px -1px rgba(0,0,0,.2)}.elevation-z8{box-shadow:0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12),0 5px 5px -3px rgba(0,0,0,.2)}.elevation-z9{box-shadow:0 9px 12px 1px rgba(0,0,0,.14),0 3px 16px 2px rgba(0,0,0,.12),0 5px 6px -3px rgba(0,0,0,.2)}.elevation-z12{box-shadow:0 12px 17px 2px rgba(0,0,0,.14),0 5px 22px 4px rgba(0,0,0,.12),0 7px 8px -4px rgba(0,0,0,.2)}.elevation-z16{box-shadow:0 16px 24px 2px rgba(0,0,0,.14),0 6px 30px 5px rgba(0,0,0,.12),0 8px 10px -5px rgba(0,0,0,.2)}.elevation-z24{box-shadow:0 24px 38px 3px rgba(0,0,0,.14),0 9px 46px 8px rgba(0,0,0,.12),0 11px 15px -7px rgba(0,0,0,.2)}.h1{font:700 32px/48px Lato}.h2{font:700 24px/32px Lato}.h3{font:700 20px/32px Lato}.h4{font:700 18px/24px Lato}.h5{font:700 16px/24px Lato}.h6{font:700 14px/24px Lato}.b1{font:400 24px/40px Lato}.b2{font:400 20px/32px Lato}.b3{font:400 18px/32px Lato}.b4{font:400 16px/24px Lato}.b5{font:400 14px/24px Lato}button.password-toggle{background-color:#fff;border:3px solid #fff;color:#0d47a1;cursor:pointer;padding:2px 4px;border-radius:4px;display:flex;justify-content:flex-end;min-width:315px;margin-bottom:8px}button.password-toggle svg{fill:#0d47a1;height:16px;width:16px;margin:6px 6px 0 3px;vertical-align:middle}button.password-toggle svg.hide-icon{position:relative;top:-2px}button.password-toggle:hover{color:#1976d2}button.password-toggle:hover svg{fill:#1976d2}button.password-toggle:active{color:#1565c0}button.password-toggle:active svg{fill:#1565c0}button.password-toggle:focus{border-color:#4fc3f7;outline:0}.sr-only{border:none;height:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;width:1px}"]
        })
    ], PasswordField);
    return PasswordField;
}());

var FormElementsModule = /** @class */ (function () {
    function FormElementsModule() {
    }
    FormElementsModule = __decorate([
        NgModule({
            imports: [
                CommonModule,
                FormsModule,
                ReactiveFormsModule,
            ],
            declarations: [
                FormElement,
                FormLabel,
                InputField,
                Select,
                SelectOption,
                FormHelpText,
                FormErrorText,
                PasswordField,
            ],
            exports: [
                FormElement,
                InputField,
                FormLabel,
                Select,
                SelectOption,
                FormHelpText,
                FormErrorText,
                PasswordField,
            ],
        })
    ], FormElementsModule);
    return FormElementsModule;
}());

/**
 * Public API Surface of ln-form-elements
 */

/*
 * Public API Surface of ln-components-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Accordion, AccordionModule, AccordionPanel, BACKSPACE, Button, ButtonGroup, ButtonGroupItem, ButtonGroupModule, ButtonModule, Card, CardContent, CardModule, CardTitle, Checkbox, CheckboxModule, DELETE, FormElement, FormElementsModule, FormErrorText, FormHelpText, FormLabel, Icon, IconModule, IconsRegistry, InputField, Modal, ModalActions, ModalContent, ModalHeader, ModalModule, Notification, NotificationModule, POPOVER_SCROLL_STRATEGY, Pagination, PaginationModule, Popover, PopoverBaseDirective, PopoverDirective, PopoverModule, Radio, RadioModule, SPACE, Select, SelectOption, SelectService, Snackbar, SnackbarModule, SnackbarService, Switch, SwitchModule, Tab, Tabs, TabsModule, Tag, Tags, TagsModule, Tooltip, TooltipDirective, TooltipModule, PopoverImage as ɵa, PopoverTitle as ɵb, PopoverHeader as ɵc, PopoverBody as ɵd, PopoverActions as ɵe, POPOVER_SCROLL_STRATEGY_FACTORY as ɵf, POPOVER_SCROLL_STRATEGY_FACTORY_PROVIDER as ɵg, prefix as ɵh, PasswordField as ɵi };
//# sourceMappingURL=ln-components-angular.js.map
